import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, ChangeDetectorRef, inject } from '@angular/core';
import { runUploadControlAction, uploadControlLoadingKey } from '../shared/upload-control-loading';
import { CommonModule } from '@angular/common';
import { ResumableUploadService, ResumableUploadSession, UploadManagerService, UploadFileItem, UploadStatus } from '@doc/api/server-bo';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import { EnvConfig } from '@doc/core';
import { DocConfirmService } from '@doc/component/shared';
import { TranslateService } from '@ngx-translate/core';

interface FolderUploadSummary {
  folderName: string;
  fileIds: string[];
  uploadedItems: number;
  totalItems: number;
  uploadedBytes: number;
  totalBytes: number;
  progress: number;
  status: UploadStatus;
  isComplete: boolean;
}

@Component({
  selector: 'doc-resumable-upload',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './resumable-upload.component.html',
  styleUrls: ['./resumable-upload.component.scss']
})
export class ResumableUploadComponent implements OnInit, OnDestroy, OnChanges {
  @Input() files: File[] = [];
  @Input() chunkSize?: number; 
  @Input() maxRetries?: number; 
  @Input() folderId?: number;
  @Input() titleEn?: string;
  @Input() titleAr?: string;
  @Input() eventId?: number;
  @Input() eventDate?: string;
  @Input() eventStartDate?: string;
  @Input() eventEndDate?: string;
  @Input() newEvent?: string;
  @Input() locationId?: number | null;
  @Input() newLocation?: string | null;
  @Input() isMarkSecure?: boolean;
  @Input() tagIds?: number[];
  @Input() newTags?: string[];
  @Input() userId: number = 1;
  @Input() fileCreationDate?: string;
  @Input() shareType: 'internal' | 'external' = 'internal';
  @Input() shareUserIds: number[] = [];
  @Input() shareEmails: string[] = [];
  @Input() shareExpireOn?: string | null;
  /** Optional approval workflow (mirrors document create Enable Workflow). */
  @Input() workflowId?: number | null;
  @Input() autoResume: boolean = false; // Auto-resume incomplete sessions in background
  @Input() autoStart: boolean = false; // Control whether to auto-start uploads
  @Input() perFileFolderIds: number[] = []; // Optional mapping of file index to folderId
  @Input() folderMetadataOnlyFolderIds: number[] = [];

  @Output() uploadComplete = new EventEmitter<{ filePath: string; fileName: string; fileSize: number }[]>();
  @Output() uploadStarted = new EventEmitter<void>(); // Emit when uploads actually start
  @Output() uploadProgress = new EventEmitter<{ file: File; progress: number }>();
  @Output() uploadError = new EventEmitter<{ file: File; error: string }>();
  @Output() allUploadsCompleted = new EventEmitter<boolean>();
  @Output() fileRemoved = new EventEmitter<{ fileName: string; fileSize: number; lastModified: number }>();
  @Output() slowInternetDetected = new EventEmitter<void>();

  uploads: UploadFileItem[] = [];
  folderUploadSummaries: FolderUploadSummary[] = [];
  get displayedUploads(): UploadFileItem[] {
    return this.uploads.filter(upload => !this.getTopFolderName(upload));
  }
  private destroy$ = new Subject<void>();
  private isProcessingQueue = false;
  private completedSessions: { filePath: string; fileName: string; fileSize: number }[] = [];
  
  // Resume control (disable auto-resume, throttle, and prevent duplicates)
  private resumeStartedOnce = false;
  private resumeQueue: UploadFileItem[] = [];
  private resumeQueueIds = new Set<string>();
  private isProcessingResumeQueue = false;
  private resumeOnlyMode = false;
  private pendingAutoResume = false;
  private readonly resumeThrottleMs = 500;
  private readonly resumeConcurrencyLimit = 1;
  private readonly resumeChunkConcurrencyLimit = 1;
  private previousConcurrencyLimits: { files: number; chunks: number } | null = null;
  readonly controlActionLoadingKeys = new Set<string>();
  private readonly onlineHandler = () => {
    void this.handleOnlineResume();
  };
  
  // 🔇 DEBUG FLAG - Set to true to enable console logging (for development/debugging)
  // Set to false to disable all console messages (for production)
  private readonly DEBUG_LOGGING = false;
  
  /** Delay between starting parallel file sessions to avoid initialize API bursts. */
  private readonly parallelInitStaggerMs = 300;

  // Speed detection and parallel upload support
  private speedMeasurementStartTime: number = 0;
  private speedMeasurementBytes: number = 0;
  private speedMeasurementChunkCount: number = 0;
  private slowInternetMessageShown: boolean = false; // Track if slow internet message has been shown
  
  // Get speed measurement interval from config or default
  private get speedMeasurementInterval(): number {
    return this.envConfig?.resumableUpload?.speedMeasurementInterval ?? 5;
  }

  // Get chunk size from input or environment config
  get effectiveChunkSize(): number {
    return this.chunkSize ?? this.envConfig?.resumableUpload?.chunkSizeBytes ?? (5 * 1024 * 1024); // Default 5MB
  }

  // Get max retries from input or environment config
  get effectiveMaxRetries(): number {
    return this.maxRetries ?? this.envConfig?.resumableUpload?.maxRetries ?? 3; // Default 3
  }

  private envConfig = inject(EnvConfig);
  private confirmationService = inject(DocConfirmService);
  private translateService = inject(TranslateService);

  constructor(
    private resumableUploadService: ResumableUploadService,
    private uploadManager: UploadManagerService,
    private cdr: ChangeDetectorRef
  ) {
    // Set upload manager in service so it can access connection speed info
    this.resumableUploadService.setUploadManager(this.uploadManager);
  }

  // 🔇 Logging helper - all console messages go through this
  // Can be easily disabled by setting DEBUG_LOGGING = false
  private log(message: string, ...args: any[]): void {
    if (this.DEBUG_LOGGING) {
      this.log(message, ...args);
    }
  }

  private warn(message: string, ...args: any[]): void {
    if (this.DEBUG_LOGGING) {
      this.warn(message, ...args);
    }
  }

  private error(message: string, ...args: any[]): void {
    if (this.DEBUG_LOGGING) {
      this.error(message, ...args);
    }
  }

  private isOffline(): boolean {
    return typeof navigator !== 'undefined' && navigator.onLine === false;
  }

  private isNetworkError(error: any): boolean {
    const status = error?.status ?? error?.statusCode;
    if (status === 0) {
      return true;
    }
    const message = (error?.message || error?.error?.message || '').toString().toLowerCase();
    return (
      message.includes('failed to fetch') ||
      message.includes('networkerror') ||
      message.includes('unknown error') ||
      message.includes('http failure response') ||
      message.includes('net::err') ||
      message.includes('gateway timeout') ||
      message.includes('service unavailable') ||
      error?.name === 'TimeoutError'
    );
  }

  private async waitForOnline(): Promise<void> {
    if (!this.isOffline()) {
      return;
    }
    await new Promise<void>((resolve) => {
      const handler = () => resolve();
      window.addEventListener('online', handler, { once: true });
    });
  }

  private async handleOnlineResume(): Promise<void> {
    if (!this.autoResume) {
      return;
    }

    // First, try to reconnect any incomplete sessions that match selected files.
    await this.autoResumeIfEnabled(this.files || []);

    const uploads = this.uploadManager.getAllUploads();
    if (uploads.length === 0) {
      return;
    }

    // If any uploads were paused (e.g., due to offline), re-queue them.
    uploads
      .filter(upload => upload.status === 'paused')
      .forEach(upload => this.uploadManager.resumeUpload(upload.fileId));

    const hasQueued = uploads.some(upload => upload.status === 'queued');
    const hasRetryableFailed = uploads.some(
      upload => upload.status === 'failed' && (upload.retryCount || 0) < this.effectiveMaxRetries
    );

    if ((hasQueued || hasRetryableFailed) && !this.isProcessingQueue) {
      // Allow normal queue processing for automatic resume.
      this.resumeOnlyMode = false;
      this.pendingAutoResume = false;
      void this.processQueue();
    }
  }

  isPlaceholderFile(file?: File): boolean {
    return !!file && (file as any).__isPlaceholder === true;
  }

  /** Uses per-upload flag from initialize API; falls back to env then true (existing default). */
  private resolveStreamThroughBackend(upload: UploadFileItem): boolean {
    if (upload.streamThroughBackend === true) {
      return true;
    }
    if (upload.streamThroughBackend === false) {
      return false;
    }
    return this.envConfig?.resumableUpload?.streamThroughBackend ?? true;
  }

  ngOnInit() {
    // Initialize concurrency limits from config if provided
    if (this.envConfig?.resumableUpload?.maxConcurrentFiles) {
      this.uploadManager.maxConcurrentFiles = this.envConfig.resumableUpload.maxConcurrentFiles;
    }
    if (this.envConfig?.resumableUpload?.maxConcurrentChunksPerFile) {
      this.uploadManager.maxConcurrentChunksPerFile = this.envConfig.resumableUpload.maxConcurrentChunksPerFile;
    }

    this.uploadManager.processingRequested$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.resumeOnlyMode = false;
        this.pendingAutoResume = false;
        if (!this.isProcessingQueue) {
          void this.processQueue();
        }
      });

    // Subscribe to uploads from manager
    this.uploadManager.uploads$
      .pipe(takeUntil(this.destroy$))
      .subscribe(uploads => {
        this.uploads = uploads;
        this.folderUploadSummaries = this.buildFolderUploadSummaries(uploads);
        this.cdr.detectChanges();
        
        // Check if all uploads are completed
        const allCompleted = this.uploadManager.areAllActiveUploadsCompleted();
        this.allUploadsCompleted.emit(allCompleted);
      });

    // Check for incomplete uploads on init (no auto-resume)
    this.checkAndResumeIncompleteUploads();
    this.autoResumeIfEnabled(this.files || []);

    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.onlineHandler);
    }

    // Auto-start queue processing only if autoStart is true
    if (this.files.length > 0 && this.autoStart) {
      this.addFilesToQueue(true);
    } else if (this.files.length > 0) {
      // Just add to queue without starting
      this.addFilesToQueue(false);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['files'] && changes['files'].currentValue) {
      const newFiles = changes['files'].currentValue;
      this.syncUploadsWithLatestFiles(newFiles);
      if (this.autoResume) {
        this.autoResumeIfEnabled(newFiles).then(() => {
          // Add remaining files to queue but do not auto-start
          this.addFilesToQueue(false);
        });
      } else {
        // Only prepare resume state; actual resume happens on user action
        this.checkAndAutoResumeFiles(newFiles, false).then(() => {
          // Add remaining files to queue but do not auto-start
          this.addFilesToQueue(false);
        });
      }
    }
  }

  private getFileSourceKey(file: File): string {
    return (file as any)?.__sourceKey || '';
  }

  private getFallbackSignature(file: File): string {
    const originalRelativePath = ((file as any)?.__originalRelativePath || (file as any)?.webkitRelativePath || file.name || '')
      .toString()
      .replace(/\\/g, '/')
      .trim();
    return `${originalRelativePath}|${file.size}|${file.lastModified}`;
  }

  private resolveFolderIdForFile(file: File): number | undefined {
    if (!this.perFileFolderIds || this.perFileFolderIds.length === 0) {
      return this.folderId;
    }

    const sourceKey = this.getFileSourceKey(file);
    if (sourceKey) {
      const sourceIndex = this.files.findIndex(candidate => this.getFileSourceKey(candidate) === sourceKey);
      if (sourceIndex >= 0 && this.perFileFolderIds[sourceIndex] !== undefined) {
        return this.perFileFolderIds[sourceIndex];
      }
    }

    const signature = this.getFallbackSignature(file);
    const signatureIndex = this.files.findIndex(candidate => this.getFallbackSignature(candidate) === signature);
    if (signatureIndex >= 0 && this.perFileFolderIds[signatureIndex] !== undefined) {
      return this.perFileFolderIds[signatureIndex];
    }

    return this.folderId;
  }

  private shouldInheritFolderMetadata(folderId?: number): boolean {
    const resolvedFolderId = Number(folderId ?? 0);
    return resolvedFolderId > 0 && this.folderMetadataOnlyFolderIds.includes(resolvedFolderId);
  }

  private buildEffectiveMetadata(
    metadata: UploadFileItem['metadata'],
    folderId?: number
  ): UploadFileItem['metadata'] {
    const resolvedFolderId = folderId ?? metadata?.folderId;
    if (!this.shouldInheritFolderMetadata(resolvedFolderId)) {
      return {
        ...metadata,
        folderId: resolvedFolderId
      };
    }

    return {
      ...metadata,
      folderId: resolvedFolderId,
      eventId: undefined,
      eventDate: undefined,
      eventStartDate: undefined,
      eventEndDate: undefined,
      locationId: undefined,
      newLocation: undefined,
      tagIds: [],
      newTags: [],
      newEvent: undefined
    };
  }

  private syncUploadsWithLatestFiles(files: File[]): void {
    if (!files || files.length === 0) {
      return;
    }

    const latestBySourceKey = new Map<string, File>();
    const latestByFallback = new Map<string, File>();
    files.forEach(file => {
      const sourceKey = this.getFileSourceKey(file);
      if (sourceKey) {
        latestBySourceKey.set(sourceKey, file);
      }
      latestByFallback.set(this.getFallbackSignature(file), file);
    });

    this.uploadManager.getAllUploads().forEach(upload => {
      const uploadSourceKey = this.getFileSourceKey(upload.file);
      const updatedFile =
        (uploadSourceKey ? latestBySourceKey.get(uploadSourceKey) : undefined) ||
        latestByFallback.get(this.getFallbackSignature(upload.file));
      if (updatedFile && updatedFile !== upload.file) {
        upload.file = updatedFile;
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.onlineHandler);
    }
  }

  private getShareEmailsString(): string | undefined {
    return this.shareEmails && this.shareEmails.length > 0
      ? this.shareEmails.join(',')
      : undefined;
  }

  private parseNumberArray(value: any): number[] {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value.map((v) => Number(v)).filter((v) => !isNaN(v));
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return [];
      try {
        if (trimmed.startsWith('[')) {
          const parsed = JSON.parse(trimmed);
          return Array.isArray(parsed)
            ? parsed.map((v: any) => Number(v)).filter((v: number) => !isNaN(v))
            : [];
        }
      } catch {
        // fall through to comma split
      }
      return trimmed
        .split(',')
        .map((v) => Number(v.trim()))
        .filter((v) => !isNaN(v));
    }
    return [];
  }

  private parseStringArray(value: any): string[] {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value.map((v) => String(v).trim()).filter((v) => v.length > 0);
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return [];
      try {
        if (trimmed.startsWith('[')) {
          const parsed = JSON.parse(trimmed);
          return Array.isArray(parsed)
            ? parsed.map((v: any) => String(v).trim()).filter((v: string) => v.length > 0)
            : [];
        }
      } catch {
        // fall through to comma split
      }
      return trimmed
        .split(',')
        .map((v) => v.trim())
        .filter((v) => v.length > 0);
    }
    return [];
  }

  /**
   * Add files to upload queue
   */
  private addFilesToQueue(startProcessing: boolean = true): void {
    if (!this.files || this.files.length === 0) return;
    const filesToQueue = this.files.filter(file => !this.isPlaceholderFile(file));
    if (filesToQueue.length === 0) return;

    const metadata = {
      folderId: this.folderId,
      titleEn: this.titleEn,
      titleAr: this.titleAr,
      eventId: this.eventId,
      eventDate: this.eventDate,
      eventStartDate: this.eventStartDate,
      eventEndDate: this.eventEndDate,
      locationId: this.locationId ?? undefined,
      newLocation: this.newLocation ?? undefined,
      tagIds: this.tagIds,
      newTags: this.newTags,
      newEvent: this.newEvent,
      isMarkSecure: this.isMarkSecure,
      userEmails: this.getShareEmailsString(),
      shareType: this.shareType,
      shareUserIds: this.shareUserIds,
      shareEmails: this.shareEmails,
      shareExpireOn: this.shareExpireOn ?? undefined,
      userId: this.userId,
      fileCreationDate: this.fileCreationDate,
      workflowId: this.workflowId && this.workflowId > 0 ? this.workflowId : undefined
    };

    const perFileMetadata = this.perFileFolderIds.length > 0
      ? filesToQueue.map((file) =>
          this.buildEffectiveMetadata(
            metadata,
            this.resolveFolderIdForFile(file) || this.folderId
          ))
      : undefined;

    this.uploadManager.addFiles(filesToQueue, metadata, perFileMetadata);
    
    // Start processing queue only if startProcessing is true
    if (startProcessing && !this.isProcessingQueue) {
      this.processQueue();
    }
  }

  /**
   * Public method to start uploads manually (called when user clicks Upload button)
   */
  public async startUploads(): Promise<void> {
    // User action should allow normal queue processing
    this.resumeOnlyMode = false;
    this.pendingAutoResume = false;

    // Resume pending sessions only when user explicitly clicks upload
    await this.triggerResumePendingUploads();

    // Add files to queue if not already added
    if (this.files && this.files.length > 0) {
      this.syncUploadsWithLatestFiles(this.files);
      const existingUploads = this.uploadManager.getAllUploads();
      const existingUploadSignatures = new Set(existingUploads.map(upload => {
        return this.getFileSourceKey(upload.file) || this.getFallbackSignature(upload.file);
      }));
      const filesInQueue = existingUploads.filter(u => 
        this.files.some(f => 
          (this.getFileSourceKey(f) && this.getFileSourceKey(u.file) && this.getFileSourceKey(f) === this.getFileSourceKey(u.file)) ||
          this.getFallbackSignature(f) === this.getFallbackSignature(u.file)
        )
      );

      // Only add files that aren't already in queue
      const filesToAdd = this.files
        .filter(file => !this.isPlaceholderFile(file))
        .filter(file => !existingUploadSignatures.has(this.getFileSourceKey(file) || this.getFallbackSignature(file)));

      if (filesToAdd.length > 0) {
        const metadata = {
          folderId: this.folderId,
          titleEn: this.titleEn,
          titleAr: this.titleAr,
          eventId: this.eventId,
          eventDate: this.eventDate,
          eventStartDate: this.eventStartDate,
          eventEndDate: this.eventEndDate,
          locationId: this.locationId ?? undefined,
          newLocation: this.newLocation ?? undefined,
          tagIds: this.tagIds,
          newTags: this.newTags,
          newEvent: this.newEvent,
          isMarkSecure: this.isMarkSecure,
          userEmails: this.getShareEmailsString(),
          shareType: this.shareType,
          shareUserIds: this.shareUserIds,
          shareEmails: this.shareEmails,
          shareExpireOn: this.shareExpireOn ?? undefined,
          userId: this.userId,
          fileCreationDate: this.fileCreationDate,
          workflowId: this.workflowId && this.workflowId > 0 ? this.workflowId : undefined
        };
        const perFileMetadata = this.perFileFolderIds.length > 0
          ? filesToAdd.map((file) =>
              this.buildEffectiveMetadata(
                metadata,
                this.resolveFolderIdForFile(file) || this.folderId
              ))
          : undefined;
        this.uploadManager.addFiles(filesToAdd, metadata, perFileMetadata);
      }

      // Ensure existing queued uploads use latest metadata
      const currentMetadata = {
        folderId: this.folderId,
        titleEn: this.titleEn,
        titleAr: this.titleAr,
        eventId: this.eventId,
        eventDate: this.eventDate,
        eventStartDate: this.eventStartDate,
        eventEndDate: this.eventEndDate,
        locationId: this.locationId ?? undefined,
        newLocation: this.newLocation ?? undefined,
        tagIds: this.tagIds,
        newTags: this.newTags,
        newEvent: this.newEvent,
        isMarkSecure: this.isMarkSecure,
        userEmails: this.getShareEmailsString(),
        shareType: this.shareType,
        shareUserIds: this.shareUserIds,
        shareEmails: this.shareEmails,
        shareExpireOn: this.shareExpireOn ?? undefined,
        userId: this.userId,
        fileCreationDate: this.fileCreationDate,
        workflowId: this.workflowId && this.workflowId > 0 ? this.workflowId : undefined
      };
      filesInQueue.forEach(upload => {
        this.uploadManager.updateMetadata(
          upload.fileId,
          this.buildEffectiveMetadata(
            currentMetadata,
            this.resolveFolderIdForFile(upload.file) || this.folderId
          )
        );
      });
    }

    const hasQueuedUploads = this.uploadManager.getAllUploads().some(upload =>
      upload.status === 'queued' || upload.status === 'failed'
    );

    // Start processing queue
    if (!this.isProcessingQueue) {
      if (hasQueuedUploads) {
        this.uploadStarted.emit();
      }
      await this.processQueue();
    }
  }

  public pauseAllUploads(): void {
    this.uploadManager.pauseAllUploads();
  }

  get hasActiveUploading(): boolean {
    return this.uploads.some(upload => upload.status === 'uploading');
  }

  /**
   * Process upload queue - supports parallel file uploads
   */
  private async processQueue(): Promise<void> {
    if (this.isProcessingQueue) return;
    this.isProcessingQueue = true;

    const activeUploads = new Set<Promise<void>>();

    while (true) {
      // Start new uploads up to the concurrency limit
      while (activeUploads.size < this.uploadManager.maxConcurrentFiles) {
        const nextFile = this.getNextQueuedUpload();
        
        if (!nextFile) {
          break; // No more files to process
        }

        // Check if we can start this upload
        if (!this.uploadManager.canStartUpload(nextFile.fileId)) {
          break; // Can't start more uploads
        }

        // Add to active uploads
        this.uploadManager.addActiveUploading(nextFile.fileId);

        // Log if this is a resume
        if (nextFile.sessionId && (nextFile as any).pendingChunks) {
          const pendingChunks = (nextFile as any).pendingChunks as number[];
          this.log(`[QUEUE] Resuming upload for ${nextFile.file.name} with ${pendingChunks.length} pending chunks`);
        }

        // Start upload (non-blocking)
        const uploadPromise = this.uploadFile(nextFile)
          .catch((error: any) => {
            this.error(`[QUEUE] Error uploading ${nextFile.file.name}:`, error);
            // Error handling is done in uploadFile method
          })
          .finally(() => {
            // Remove from active uploads
            this.uploadManager.removeActiveUploading(nextFile.fileId);
            activeUploads.delete(uploadPromise);
          });

        activeUploads.add(uploadPromise);

        if (activeUploads.size < this.uploadManager.maxConcurrentFiles) {
          await new Promise(resolve => setTimeout(resolve, this.parallelInitStaggerMs));
        }
      }

      // If no active uploads and no more files, we're done
      if (activeUploads.size === 0) {
        const nextFile = this.uploadManager.getNextQueuedFile();
        if (!nextFile) {
          this.isProcessingQueue = false;
          break;
        }
      }

      // Wait for at least one upload to complete before checking for more
      if (activeUploads.size > 0) {
        await Promise.race(activeUploads);
      } else {
        // Small delay before checking again
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
  }

  /**
   * Upload a single file
   */
  private async uploadFile(uploadItem: UploadFileItem): Promise<void> {
    const fileId = uploadItem.fileId;

    try {
      // CRITICAL: Validate file before starting upload
      // This is especially important for resume scenarios where placeholder files might exist
      if (!uploadItem.file || uploadItem.file.size === 0) {
        // Try to find matching file from input files
        const matchingFile = this.files.find(f => 
          f.name === uploadItem.file.name && 
          f.size === uploadItem.totalBytes
        );
        
        if (matchingFile) {
          this.log(`[UPLOAD] Replacing invalid file reference with actual file: ${matchingFile.name} (${matchingFile.size} bytes)`);
          uploadItem.file = matchingFile;
          // Update the upload manager's reference as well
          const currentItem = this.uploadManager.getUpload(fileId);
          if (currentItem) {
            currentItem.file = matchingFile;
          }
        } else {
          throw new Error(`Cannot upload - file "${uploadItem.file?.name || 'unknown'}" is invalid or empty (size: ${uploadItem.file?.size || 0}, expected: ${uploadItem.totalBytes}). Please select the file first.`);
        }
      }
      
      // Additional validation: ensure file size matches totalBytes
      if (uploadItem.file.size !== uploadItem.totalBytes) {
        this.warn(`[UPLOAD] File size mismatch: file.size=${uploadItem.file.size}, totalBytes=${uploadItem.totalBytes}. Using file.size as source of truth.`);
        uploadItem.totalBytes = uploadItem.file.size;
      }
      
      // STEP 1: Initialize session (only if uploadUrl/filePath not set)
      // For resume, these should already be set from checkAndAutoResumeFiles
      const currentItem = this.uploadManager.getUpload(fileId);
      if (!currentItem?.uploadUrl || !currentItem?.filePath) {
        this.log(`[UPLOAD] Initializing new session for ${uploadItem.file.name}`);
        this.uploadManager.updateStatus(fileId, 'initializing');
        
        const sessionData = await this.initializeSession(uploadItem);
        
        // Get updated item to check streamThroughBackend flag
        const updatedItem = this.uploadManager.getUpload(fileId);
        
        this.uploadManager.updateSessionData(fileId, {
          uploadUrl: sessionData.uploadUrl,
          filePath: sessionData.filePath,
          fileRecordId: sessionData.fileRecordId,
          chunkSize: this.effectiveChunkSize,
          sessionId: sessionData.sessionId,
          bytesUploaded: sessionData.bytesUploaded || 0
        });
        
        // Ensure streamThroughBackend flag is preserved
        if (updatedItem?.streamThroughBackend !== undefined) {
          const finalItem = this.uploadManager.getUpload(fileId);
          if (finalItem) {
            finalItem.streamThroughBackend = updatedItem.streamThroughBackend;
          }
        }
      } else {
        // Resume scenario - session already initialized, use existing data
        this.log(`[UPLOAD] Resuming upload for ${uploadItem.file.name} with existing session: uploadUrl=${!!currentItem.uploadUrl}, filePath=${!!currentItem.filePath}, sessionId=${currentItem.sessionId}`);
        // Update uploadItem reference to use current item with session data
        uploadItem = currentItem;
      }

      // Check if cancelled during initialization
      const finalCurrentItem = this.uploadManager.getUpload(fileId);
      if (!finalCurrentItem || finalCurrentItem.status === 'cancelled') {
        return;
      }

      // STEP 2: Upload chunks (with parallel support)
      // Use the current item from manager (has latest session data, especially for resume)
      const itemToUpload = finalCurrentItem;
      this.uploadManager.updateStatus(fileId, 'uploading');
      
      // Create abort controller if not exists
      if (!itemToUpload.abortController) {
        itemToUpload.abortController = new AbortController();
      }

      // If this is the first file and we haven't measured speed yet, start measurement
      if (this.uploadManager.currentUploadSpeed === 0) {
        this.speedMeasurementStartTime = Date.now();
        this.speedMeasurementBytes = 0;
        this.speedMeasurementChunkCount = 0;
      }

      this.log(`[UPLOAD] Starting chunk upload for ${itemToUpload.file.name}: uploadUrl=${!!itemToUpload.uploadUrl}, filePath=${!!itemToUpload.filePath}, sessionId=${itemToUpload.sessionId}`);
      await this.uploadChunks(itemToUpload);

      // Check status after chunk upload
      const afterChunks = this.uploadManager.getUpload(fileId);
      if (!afterChunks || afterChunks.status === 'cancelled' || afterChunks.status === 'paused') {
        return;
      }

      // STEP 3: Verify upload
      await this.verifyUpload(afterChunks);

    } catch (error: any) {
      const currentItem = this.uploadManager.getUpload(fileId);
      if (!currentItem || currentItem.status === 'cancelled') {
        return;
      }

      if (this.isOffline() || this.isNetworkError(error)) {
        this.uploadManager.updateStatus(fileId, 'paused');
        return;
      }

      if (error?.isUrlExpired && !(uploadItem as any)._restartedFromExpired) {
        (uploadItem as any)._restartedFromExpired = true;
        this.warn(`[UPLOAD] Upload URL expired for ${uploadItem.file.name}. Creating a new session and restarting from byte 0.`);
        try {
          await this.restartExpiredUploadSession(uploadItem);
          await this.uploadChunks(uploadItem);
          await this.verifyUpload(uploadItem);
          return;
        } catch (restartError) {
          this.error('[UPLOAD] Failed to restart expired session:', restartError);
        }
      }

      const errorMessage = error?.message || 'Upload failed';

      // Check if it's a CORS error on last chunk (treat as success)
      if (this.isLastChunkCORSError(error, uploadItem)) {
        this.log('[UPLOAD] Last chunk CORS error detected - treating as success');
        await this.verifyUpload(uploadItem);
        return;
      }

      // Check retry count
      const retryCount = uploadItem.retryCount || 0;
      if (retryCount < this.effectiveMaxRetries) {
        this.uploadManager.incrementRetryCount(fileId);
        this.uploadManager.updateStatus(fileId, 'queued');
        // Will be retried by queue processor
        return;
      }

      // Max retries reached
      this.uploadManager.updateStatus(fileId, 'failed', errorMessage);
      this.uploadError.emit({ file: uploadItem.file, error: errorMessage });
    }
  }

  private async restartExpiredUploadSession(uploadItem: UploadFileItem): Promise<void> {
    const fileId = uploadItem.fileId;
    const oldSessionId = uploadItem.sessionId;
    if (oldSessionId) {
      this.pendingChunksCache.delete(oldSessionId);
    }
    delete (uploadItem as any).pendingChunks;

    this.uploadManager.updateStatus(fileId, 'initializing');
    this.uploadManager.updateProgress(fileId, 0);
    uploadItem.bytesUploaded = 0;
    uploadItem.sessionId = undefined;
    uploadItem.uploadUrl = undefined;
    uploadItem.filePath = undefined;

    const sessionData = await this.initializeSession(uploadItem);
    this.uploadManager.updateSessionData(fileId, {
      uploadUrl: sessionData.uploadUrl,
      filePath: sessionData.filePath,
      fileRecordId: sessionData.fileRecordId,
      chunkSize: this.effectiveChunkSize,
      sessionId: sessionData.sessionId,
      bytesUploaded: 0
    });
  }

  /**
   * Initialize upload session
   */
  private async initializeSession(uploadItem: UploadFileItem): Promise<{
    uploadUrl: string;
    filePath: string;
    fileRecordId?: string;
    sessionId?: string;
    bytesUploaded?: number;
  }> {
    const session: ResumableUploadSession = {
      userId: uploadItem.metadata?.userId || this.userId,
      fileName: uploadItem.file.name,
      fileSize: uploadItem.totalBytes,
      contentType: uploadItem.file.type || 'application/octet-stream',
      chunkSize: this.effectiveChunkSize,
      folderId: uploadItem.metadata?.folderId,
      libraryType: uploadItem.metadata?.libraryType,
      departmentId: uploadItem.metadata?.departmentId,
      titleEn: uploadItem.metadata?.titleEn,
      titleAr: uploadItem.metadata?.titleAr,
      eventId: uploadItem.metadata?.eventId,
      eventDate: uploadItem.metadata?.eventDate,
      eventStartDate: uploadItem.metadata?.eventStartDate,
      eventEndDate: uploadItem.metadata?.eventEndDate,
      locationId: uploadItem.metadata?.locationId,
      newLocation: uploadItem.metadata?.newLocation,
      tagIds: uploadItem.metadata?.tagIds?.join(','),
      newTags: uploadItem.metadata?.newTags?.join(','),
      newEvent: uploadItem.metadata?.newEvent,
      isMarkSecure: uploadItem.metadata?.isMarkSecure,
      userEmails: uploadItem.metadata?.userEmails,
      userIds: uploadItem.metadata?.shareUserIds,
      emails: uploadItem.metadata?.shareEmails,
      expireOn: uploadItem.metadata?.shareExpireOn || undefined,
      shareType: uploadItem.metadata?.shareType,
      fileCreationDate: uploadItem.metadata?.fileCreationDate,
      workflowId: uploadItem.metadata?.workflowId,
      IsUploaded: true,
    };

    try {
      const response = await firstValueFrom(
        this.resumableUploadService.initializeUpload(session)
      );

      const data = response?.data || response;
      const uploadUrl = data?.uploadUrl || data?.UploadUrl || data?.upload_url;
      let filePath = data?.filePath || data?.FilePath || data?.file_path;
      const fileRecordId = data?.fileRecordId || data?.FileRecordId;
      const streamThroughBackend = data?.streamThroughBackend ?? data?.StreamThroughBackend ?? true;
      const sessionId = data?.sessionId || data?.SessionId;
      const isResume = data?.isResume || data?.IsResume || false;
      const bytesUploaded = data?.bytesUploaded || data?.BytesUploaded || 0;

      // Extract filePath from uploadUrl if not provided
      if (!filePath && uploadUrl) {
        try {
          const urlObj = new URL(uploadUrl);
          const nameParam = urlObj.searchParams.get('name');
          if (nameParam) {
            filePath = decodeURIComponent(nameParam);
          }
        } catch (e) {
          this.warn('[INIT] Could not extract filePath from uploadUrl:', e);
        }
      }

      if (!uploadUrl) {
        throw new Error('Failed to initialize: Missing uploadUrl in response');
      }

      if (!filePath) {
        throw new Error('Failed to initialize: Missing filePath in response');
      }

      // Store streamThroughBackend flag in uploadItem
      const currentItem = this.uploadManager.getUpload(uploadItem.fileId);
      if (currentItem) {
        currentItem.streamThroughBackend = streamThroughBackend;
        this.log(`[INIT] Stream through backend: ${streamThroughBackend}, SessionId: ${sessionId || 'none'}, IsResume: ${isResume}`);
      }

      return { 
        uploadUrl, 
        filePath, 
        fileRecordId, 
        sessionId: sessionId || undefined,
        bytesUploaded: bytesUploaded || 0
      };
    } catch (error: any) {
      throw new Error(`Failed to initialize upload session: ${error.message || error}`);
    }
  }

  /**
   * Upload chunks with parallel support (maintains sequential order for GCS)
   */
  private async uploadChunks(uploadItem: UploadFileItem): Promise<void> {
    const fileId = uploadItem.fileId;
    
    // CRITICAL: Get latest upload item and validate file before starting chunk uploads
    const latestItem = this.uploadManager.getUpload(fileId);
    if (!latestItem) {
      throw new Error(`Upload item not found: ${fileId}`);
    }
    
    // Use the latest file reference
    let fileToUse = latestItem.file;
    
    // CRITICAL: Validate file before starting chunk uploads
    if (!fileToUse || fileToUse.size === 0 || fileToUse.size !== latestItem.totalBytes) {
      this.warn(`[UPLOAD CHUNKS] Invalid file reference: file=${!!fileToUse}, fileSize=${fileToUse?.size || 0}, totalBytes=${latestItem.totalBytes}`);
      
      // Try to find matching file from input files
      const matchingFile = this.files.find(f => 
        f.name === latestItem.file.name && 
        f.size === latestItem.totalBytes
      );
      
      if (matchingFile) {
        this.log(`[UPLOAD CHUNKS] ✅ Replacing invalid file reference with actual file: ${matchingFile.name} (${matchingFile.size} bytes)`);
        fileToUse = matchingFile;
        uploadItem.file = matchingFile;
        latestItem.file = matchingFile;
      } else {
        throw new Error(`Cannot upload chunks - file "${latestItem.file?.name || 'unknown'}" is invalid or empty (size: ${fileToUse?.size || 0}, expected: ${latestItem.totalBytes}). Please select the file first.`);
      }
    }
    
    // CRITICAL: ALL chunks MUST go through backend API - never upload directly to GCS from UI
    // This ensures proper chunk tracking in database and consistent behavior
    if (latestItem.sessionId) {
      this.log(`[UPLOAD CHUNKS] ✅ Resume scenario detected (sessionId=${latestItem.sessionId}) - chunks will go through backend API`);
    } else {
      this.log(`[UPLOAD CHUNKS] ✅ New upload (no sessionId) - chunks will go through backend API`);
    }
    
    // Use chunkSize from uploadItem (from database) or fallback to effectiveChunkSize
    const chunkSize = uploadItem.chunkSize || this.effectiveChunkSize;
    const totalChunks = Math.ceil(uploadItem.totalBytes / chunkSize);
    
    this.log(`[UPLOAD CHUNKS] Starting chunk uploads: file="${fileToUse.name}" (${fileToUse.size} bytes), chunkSize=${chunkSize}, totalChunks=${totalChunks}, sessionId=${latestItem.sessionId || 'none'}`);
    
    // For resume scenarios, query GCS only when NOT streaming through backend.
    // When streaming through backend, rely on stored progress to avoid direct GCS calls.
    let actualBytesUploaded = uploadItem.bytesUploaded;
    if (uploadItem.sessionId && uploadItem.uploadUrl && !uploadItem.streamThroughBackend) {
      try {
        this.log(`[RESUME] Querying GCS for actual upload progress (stored: ${uploadItem.bytesUploaded} bytes)`);
        const gcsBytesUploaded = await this.resumableUploadService.getGCSUploadProgress(
          uploadItem.uploadUrl,
          uploadItem.totalBytes
        );
        
        // Handle GCS response:
        // -1 = 503 error or temporary failure (use stored value but be cautious)
        // >= 0 = valid value (use as source of truth)
        if (gcsBytesUploaded === -1) {
          // 503 or temporary error - use stored value but add safety margin
          this.warn(`[RESUME] GCS query returned error (503?), using stored value: ${uploadItem.bytesUploaded} bytes. Will add safety check.`);
          actualBytesUploaded = uploadItem.bytesUploaded;
          // Don't update progress, keep stored value
        } else if (gcsBytesUploaded >= 0) {
          // Valid GCS response - use as source of truth
          if (gcsBytesUploaded !== uploadItem.bytesUploaded) {
            this.log(`[RESUME] GCS reports ${gcsBytesUploaded} bytes uploaded (stored: ${uploadItem.bytesUploaded}). Using GCS value as source of truth.`);
          } else {
            this.log(`[RESUME] GCS confirms ${gcsBytesUploaded} bytes uploaded (matches stored value)`);
          }
          actualBytesUploaded = gcsBytesUploaded;
          // Update the upload item with actual bytes from GCS
          this.uploadManager.updateProgress(fileId, actualBytesUploaded);
          uploadItem.bytesUploaded = actualBytesUploaded;
        }
      } catch (error) {
        this.warn(`[RESUME] Could not query GCS progress, using stored value: ${uploadItem.bytesUploaded}`, error);
        // Continue with stored value if query fails, but log warning
        // actualBytesUploaded already set to uploadItem.bytesUploaded above
      }
    }
    
    // Recalculate chunks from actualBytesUploaded. If GCS checks are disabled,
    // this uses stored progress from backend instead.
    // actualBytesUploaded is the NEXT byte to upload.
    // So if actualBytesUploaded = 41943040, that means bytes 0-41943039 are uploaded
    // 
    // CRITICAL: We need to start from the EXACT byte GCS has, not from chunk boundaries
    // If GCS has 42000000 bytes, we must continue from byte 42000000, not from chunk 8 boundary (41943040)
    // 
    // Example: actualBytesUploaded = 42000000, chunkSize = 5242880
    //   - Chunk 7 ends at: 7 * 5242880 + 5242880 - 1 = 41943039
    //   - Chunk 8 starts at: 8 * 5242880 = 41943040
    //   - But GCS has 42000000 bytes, so chunk 8 is partially uploaded (41943040 to 42000000)
    //   - We need to continue from byte 42000000, which is inside chunk 8
    //   - So we start from chunk 8, but with startByte = 42000000 (not 41943040)
    let actualStartChunkIndex = Math.floor(actualBytesUploaded / chunkSize);
    
    // Build array of chunks to upload starting from the chunk that contains actualBytesUploaded
    // Note: The first chunk might be partially uploaded, so we'll adjust startByte for that chunk
    let chunksToUpload = Array.from({ length: totalChunks - actualStartChunkIndex }, (_, i) => actualStartChunkIndex + i);
    
    this.log(`[RESUME] Recalculated from GCS: ${chunksToUpload.length} chunks to upload starting from chunk ${actualStartChunkIndex} (${actualBytesUploaded} bytes uploaded, chunkSize: ${chunkSize})`);
    this.log(`[RESUME] Chunks to upload: [${chunksToUpload.slice(0, 10).join(', ')}${chunksToUpload.length > 10 ? '...' : ''}]`);
    
    // Validate: Ensure we're not trying to upload chunks that are already complete
    if (chunksToUpload.length === 0 && actualBytesUploaded < uploadItem.totalBytes) {
      this.warn(`[RESUME] No chunks to upload but upload incomplete (${actualBytesUploaded}/${uploadItem.totalBytes} bytes). This might indicate a calculation error.`);
    }
    
    // Additional validation: Log the first chunk's start byte to verify calculation
    if (chunksToUpload.length > 0) {
      const firstChunkStartByte = chunksToUpload[0] * chunkSize;
      this.log(`[RESUME] First chunk ${chunksToUpload[0]} starts at byte ${firstChunkStartByte}, actualBytesUploaded: ${actualBytesUploaded}`);
      if (firstChunkStartByte !== actualBytesUploaded) {
        this.warn(`[RESUME] WARNING: First chunk start byte (${firstChunkStartByte}) does not match actualBytesUploaded (${actualBytesUploaded}). This may cause Content-Range errors.`);
      }
    }
    
    // Track which chunks are ready to upload (previous chunk must be complete)
    let nextChunkIndex = 0; // Index into chunksToUpload array
    const chunkPromises = new Map<number, Promise<number>>();
    const completedChunks = new Set<number>();

    // Initialize speed measurement on first chunk
    if (chunksToUpload.length > 0 && chunksToUpload[0] === 0) {
      this.speedMeasurementStartTime = Date.now();
      this.speedMeasurementBytes = 0;
      this.speedMeasurementChunkCount = 0;
    }

    while (nextChunkIndex < chunksToUpload.length || chunkPromises.size > 0) {
      // Check status
      const currentItem = this.uploadManager.getUpload(fileId);
      if (!currentItem || currentItem.status === 'cancelled' || currentItem.status === 'paused') {
        // Cancel all pending chunks
        chunkPromises.forEach(p => {
          // Chunks will check status and abort themselves
        });
        return;
      }

      // Start new chunks up to the concurrency limit
      // GCS requires sequential chunks - we can upload chunks in parallel, but only consecutive ones
      // where the previous chunk is already confirmed complete
      while (
        nextChunkIndex < chunksToUpload.length &&
        chunkPromises.size < this.uploadManager.maxConcurrentChunksPerFile &&
        this.uploadManager.canUploadMoreChunks(fileId)
      ) {
        const chunkIndex = chunksToUpload[nextChunkIndex];
        
        // CRITICAL: Skip chunks that are already completed or already in progress
        if (completedChunks.has(chunkIndex)) {
          this.log(`[CHUNK] Skipping chunk ${chunkIndex} - already completed`);
          nextChunkIndex++;
          continue;
        }
        
        // CRITICAL: Skip chunks that are already in progress (in chunkPromises)
        if (chunkPromises.has(chunkIndex)) {
          this.log(`[CHUNK] Skipping chunk ${chunkIndex} - already in progress`);
          nextChunkIndex++;
          continue;
        }
        
        // CRITICAL: For GCS resumable uploads, chunks MUST be uploaded sequentially
        // We can only start chunk N if chunk N-1 is confirmed complete
        // 
        // CRITICAL FIX: For the first chunk in resume, use actualBytesUploaded as startByte
        // This handles cases where GCS has partial chunks (e.g., 42000000 bytes when chunk 8 starts at 41943040)
        // For subsequent chunks, use chunk boundary
        const isFirstChunkInResume = nextChunkIndex === 0 && chunksToUpload[0] === chunkIndex;
        const chunkBoundaryStartByte = chunkIndex * chunkSize;
        const startByte = isFirstChunkInResume && actualBytesUploaded > chunkBoundaryStartByte
          ? actualBytesUploaded  // Resume from exact byte GCS has
          : chunkBoundaryStartByte;  // Normal chunk boundary
        const endByte = Math.min(startByte + chunkSize - 1, uploadItem.totalBytes - 1);
        const isLastChunk = chunkIndex === totalChunks - 1;
        
        // Check if we can start this chunk (first chunk or previous chunk is completed)
        const previousChunkIndex = nextChunkIndex > 0 ? chunksToUpload[nextChunkIndex - 1] : -1;
        const canStartChunk = 
          nextChunkIndex === 0 || 
          completedChunks.has(previousChunkIndex);
        
        if (!canStartChunk) {
          // Can't start next chunk yet - wait for previous to complete
          this.log(`[CHUNK] Cannot start chunk ${chunkIndex} - waiting for previous chunk ${previousChunkIndex} to complete`);
          break;
        }

        // ADDITIONAL SAFETY: Verify that startByte matches current actualBytesUploaded
        // This prevents starting a chunk that's ahead of what GCS has
        // For the first chunk in resume, startByte should equal actualBytesUploaded (or be >= if partial chunk)
        // For subsequent chunks, startByte should equal actualBytesUploaded (updated after previous chunk completes)
        // 
        // CRITICAL: Re-read actualBytesUploaded from uploadItem to get latest value after chunk completions
        // The local variable might be stale if updated in promise callback
        const currentBytesUploaded = uploadItem.bytesUploaded || actualBytesUploaded;
        
        // For first chunk in resume, startByte might be > currentBytesUploaded if we're resuming from partial chunk
        // This is OK - we're continuing from where GCS left off
        if (startByte > currentBytesUploaded && !isFirstChunkInResume) {
          // Previous chunk hasn't completed yet - wait for it (only for non-first chunks)
          this.log(`[CHUNK] Waiting for previous chunk to complete before starting chunk ${chunkIndex} (startByte: ${startByte}, currentBytesUploaded: ${currentBytesUploaded}, actualBytesUploaded: ${actualBytesUploaded})`);
          // Update actualBytesUploaded from uploadItem to ensure we have latest value
          actualBytesUploaded = currentBytesUploaded;
          break;
        }
        
        // If startByte < currentBytesUploaded, this chunk is already complete (skip it)
        if (startByte < currentBytesUploaded) {
          this.log(`[CHUNK] Chunk ${chunkIndex} already complete (startByte: ${startByte} < currentBytesUploaded: ${currentBytesUploaded}). Skipping.`);
          completedChunks.add(chunkIndex);
          nextChunkIndex++;
          continue;
        }

        this.log(`[CHUNK] Preparing to upload chunk ${chunkIndex}/${totalChunks} (startByte: ${startByte}, endByte: ${endByte}, actualBytesUploaded: ${actualBytesUploaded})`);
        
        // CRITICAL Safety check: Ensure startByte is not less than actual bytes uploaded
        // actualBytesUploaded is the NEXT byte to upload, so:
        // - If startByte < actualBytesUploaded: chunk is already complete, skip it
        // - If startByte == actualBytesUploaded: chunk starts exactly where we need to start, upload it
        // - If startByte > actualBytesUploaded: we're skipping bytes, this is an error
        if (startByte < actualBytesUploaded) {
          this.warn(`[CHUNK] Skipping chunk ${chunkIndex}: startByte ${startByte} < actualBytesUploaded ${actualBytesUploaded}. This chunk should already be complete.`);
          // Mark as completed and continue to next chunk
          completedChunks.add(chunkIndex);
          continue;
        }
        
        if (startByte > actualBytesUploaded) {
          this.error(`[CHUNK] ERROR: Chunk ${chunkIndex} startByte ${startByte} > actualBytesUploaded ${actualBytesUploaded}. We're skipping bytes! This will cause Content-Range errors.`);
          throw new Error(`Chunk calculation error: Chunk ${chunkIndex} startByte ${startByte} exceeds actualBytesUploaded ${actualBytesUploaded}. Cannot resume upload.`);
        }
        
        // startByte == actualBytesUploaded is correct - this is the chunk we need to upload
        this.log(`[CHUNK] ✅ Starting upload of chunk ${chunkIndex}/${totalChunks} (startByte: ${startByte}, endByte: ${endByte}, size: ${((endByte - startByte + 1) / 1024 / 1024).toFixed(2)} MB)`);
        
        // Additional validation: For the first chunk in chunksToUpload array
        // If resuming from partial chunk, startByte will be > chunk boundary, which is OK
        const currentBytesUploadedForValidation = uploadItem.bytesUploaded || actualBytesUploaded;
        const isFirstChunkInResumeArray = chunkIndex === chunksToUpload[0];
        if (isFirstChunkInResumeArray) {
          // For first chunk in resume, startByte should be >= actualBytesUploaded (from initial GCS query)
          // If startByte < actualBytesUploaded, the chunk is already complete
          if (startByte < actualBytesUploaded) {
            this.warn(`[CHUNK] WARNING: First chunk ${chunkIndex} startByte ${startByte} < actualBytesUploaded ${actualBytesUploaded}. This chunk should already be complete.`);
            // Skip this chunk and continue
            completedChunks.add(chunkIndex);
            nextChunkIndex++;
            continue;
          } else if (startByte === actualBytesUploaded) {
            this.log(`[CHUNK] ✅ First chunk ${chunkIndex} validation passed: startByte ${startByte} == actualBytesUploaded ${actualBytesUploaded}`);
          } else {
            // startByte > actualBytesUploaded means we're resuming from partial chunk - this is OK
            this.log(`[CHUNK] ✅ First chunk ${chunkIndex} resuming from partial chunk: startByte ${startByte} > actualBytesUploaded ${actualBytesUploaded} (continuing from exact byte)`);
          }
        }

        // Increment concurrent chunk count
        this.uploadManager.incrementConcurrentChunks(fileId);

        // Start chunk upload
        const chunkPromise = this.processChunk(
          uploadItem,
          chunkIndex,
          startByte,
          endByte,
          totalChunks,
          isLastChunk
        )
          .then(async (uploadedBytes) => {
            // After chunk completes, optionally re-query GCS to get updated actualBytesUploaded.
            // Skip when streaming through backend to avoid direct GCS calls.
            if (uploadItem.uploadUrl && uploadItem.sessionId && !uploadItem.streamThroughBackend) {
              try {
                const gcsBytesUploaded = await this.resumableUploadService.getGCSUploadProgress(
                  uploadItem.uploadUrl,
                  uploadItem.totalBytes
                );
                
                // Update actualBytesUploaded with GCS value (source of truth)
                if (gcsBytesUploaded > actualBytesUploaded) {
                  this.log(`[CHUNK] Chunk ${chunkIndex} completed. GCS reports ${gcsBytesUploaded} bytes uploaded (was ${actualBytesUploaded}). Updating progress.`);
                  actualBytesUploaded = gcsBytesUploaded;
                  this.uploadManager.updateProgress(fileId, gcsBytesUploaded);
                  uploadItem.bytesUploaded = gcsBytesUploaded;
                }
              } catch (gcsError) {
                this.warn(`[CHUNK] Could not query GCS progress after chunk ${chunkIndex} completion:`, gcsError);
                // Continue with uploadedBytes value
              }
            }
            
            completedChunks.add(chunkIndex);
            chunkPromises.delete(chunkIndex);
            this.uploadManager.decrementConcurrentChunks(fileId);
            return uploadedBytes;
          })
          .catch(async (error) => {
            chunkPromises.delete(chunkIndex);
            this.uploadManager.decrementConcurrentChunks(fileId);
            
            // If chunk fails with Content-Range error, optionally check GCS to see if it was uploaded.
            // Skip when streaming through backend to avoid direct GCS calls.
            if ((error?.message?.includes('Content-Range') || error?.message?.includes('upload offset exceeds')) && !uploadItem.streamThroughBackend) {
              this.warn(`[CHUNK] Content-Range error for chunk ${chunkIndex}, checking GCS to see if chunk was actually uploaded...`);
              
              try {
                // Re-query GCS to get actual progress
                if (uploadItem.uploadUrl) {
                  const gcsBytesUploaded = await this.resumableUploadService.getGCSUploadProgress(
                    uploadItem.uploadUrl,
                    uploadItem.totalBytes
                  );
                  
                  // If GCS shows more bytes uploaded than our startByte, the chunk was actually uploaded
                  if (gcsBytesUploaded > startByte) {
                    this.log(`[CHUNK] Chunk ${chunkIndex} was actually uploaded to GCS (GCS has ${gcsBytesUploaded} bytes, chunk starts at ${startByte}). Marking as completed.`);
                    // Mark as completed and update progress
                    completedChunks.add(chunkIndex);
                    const newBytesUploaded = Math.max(uploadItem.bytesUploaded, gcsBytesUploaded);
                    actualBytesUploaded = newBytesUploaded; // Update actualBytesUploaded
                    this.uploadManager.updateProgress(fileId, newBytesUploaded);
                    uploadItem.bytesUploaded = newBytesUploaded;
                    // Return the bytes uploaded instead of throwing error
                    return endByte + 1;
                  } else {
                    this.warn(`[CHUNK] Chunk ${chunkIndex} was NOT uploaded (GCS has ${gcsBytesUploaded} bytes, chunk starts at ${startByte}). Will retry.`);
                  }
                }
              } catch (gcsError) {
                this.warn(`[CHUNK] Could not verify GCS progress after Content-Range error:`, gcsError);
              }
            }
            
            // Re-throw error if chunk was not uploaded
            throw error;
          });

        chunkPromises.set(chunkIndex, chunkPromise);
      }

      // Wait for at least one chunk to complete
      if (chunkPromises.size > 0) {
        try {
          await Promise.race(Array.from(chunkPromises.values()));
          // CRITICAL: After a chunk completes, update actualBytesUploaded from uploadItem
          // This ensures the next iteration of the while loop sees the updated value
          actualBytesUploaded = uploadItem.bytesUploaded || actualBytesUploaded;
        } catch (error: any) {
          // Handle errors - check if we should continue or abort
          const itemAfterError = this.uploadManager.getUpload(fileId);
          if (!itemAfterError || itemAfterError.status === 'cancelled' || itemAfterError.status === 'paused') {
            return;
          }
          
          // For CORS errors on last chunk, treat as success
          const isLastChunk = Array.from(chunkPromises.keys()).some(idx => idx === totalChunks - 1);
          if (isLastChunk && this.isCORSError(error)) {
            this.log('[UPLOAD] Last chunk CORS error - treating as success');
            this.uploadManager.updateProgress(fileId, uploadItem.totalBytes);
            return;
          }

          // For Content-Range errors, the chunk might have been uploaded
          // The error handler in the chunk promise will check GCS and mark as completed if needed
          // Don't re-throw here - let the loop continue to process remaining chunks
          // Only abort if it's a critical error (expired URL, cancelled, etc.)
          if (error?.message?.includes('expired') || error?.message?.includes('cancelled')) {
          throw error;
          }
          
          // For other errors, log and continue - the chunk error handler will deal with it
          this.warn(`[UPLOAD] Chunk error occurred, but continuing with remaining chunks:`, error.message);
        }
      } else {
        // No active chunks but still have chunks to process - check if we're stuck
        if (nextChunkIndex >= chunksToUpload.length) {
          // All chunks have been processed, but some might have failed
          // Optionally re-query GCS to get actual progress and see if upload is complete
          if (uploadItem.uploadUrl && uploadItem.sessionId && !uploadItem.streamThroughBackend) {
            try {
              const gcsBytesUploaded = await this.resumableUploadService.getGCSUploadProgress(
                uploadItem.uploadUrl,
                uploadItem.totalBytes
              );
              
              if (gcsBytesUploaded >= uploadItem.totalBytes) {
                this.log(`[UPLOAD] ✅ Upload complete according to GCS (${gcsBytesUploaded} bytes)`);
                this.uploadManager.updateProgress(fileId, uploadItem.totalBytes);
                return;
              } else if (gcsBytesUploaded > uploadItem.bytesUploaded) {
                this.log(`[UPLOAD] GCS shows more progress (${gcsBytesUploaded} bytes), updating...`);
                this.uploadManager.updateProgress(fileId, gcsBytesUploaded);
                uploadItem.bytesUploaded = gcsBytesUploaded;
                // Recalculate chunks and continue
                const newStartChunkIndex = Math.floor(gcsBytesUploaded / chunkSize);
                if (newStartChunkIndex > actualStartChunkIndex) {
                  this.log(`[UPLOAD] More chunks were uploaded, continuing from chunk ${newStartChunkIndex}`);
                  // Reset nextChunkIndex to continue from new position
                  const newChunksToUpload = Array.from({ length: totalChunks - newStartChunkIndex }, (_, i) => newStartChunkIndex + i);
                  chunksToUpload.length = 0;
                  chunksToUpload.push(...newChunksToUpload);
                  nextChunkIndex = 0;
                  actualBytesUploaded = gcsBytesUploaded;
                  actualStartChunkIndex = newStartChunkIndex;
                  continue;
                }
              }
            } catch (gcsError) {
              this.warn(`[UPLOAD] Could not query GCS progress:`, gcsError);
            }
          }
        }
        
        // Small delay before checking again
        await new Promise(resolve => setTimeout(resolve, 50));
      }

      // Check if upload is complete
      const itemForCompletion = this.uploadManager.getUpload(fileId);
      if (itemForCompletion && itemForCompletion.bytesUploaded >= uploadItem.totalBytes) {
        this.log(`[UPLOAD] ✅ All chunks uploaded for ${uploadItem.file.name}`);
        return;
      }
    }
  }

  /**
   * Process a single chunk (read and upload)
   */
  private async processChunk(
    uploadItem: UploadFileItem,
    chunkIndex: number,
    startByte: number,
    endByte: number,
    totalChunks: number,
    isLastChunk: boolean
  ): Promise<number> {
    const fileId = uploadItem.fileId;

    // CRITICAL: Always get the latest upload item from manager to ensure we have the correct file reference
    // This is especially important during resume when file references might be updated
    const latestUploadItem = this.uploadManager.getUpload(fileId);
    if (!latestUploadItem) {
      throw new Error(`Upload item not found for fileId: ${fileId}`);
    }
    
    // Use the latest file reference from the upload manager
    let fileToRead = latestUploadItem.file;
    
    // CRITICAL: Validate file before reading chunk
    // This prevents reading from placeholder files during resume
    if (!fileToRead || fileToRead.size === 0 || fileToRead.size !== latestUploadItem.totalBytes) {
      this.warn(`[CHUNK ${chunkIndex}] Invalid file reference: file=${!!fileToRead}, fileSize=${fileToRead?.size || 0}, totalBytes=${latestUploadItem.totalBytes}`);
      
      // Try to find matching file from input files
      const matchingFile = this.files.find(f => 
        f.name === latestUploadItem.file.name && 
        f.size === latestUploadItem.totalBytes
      );
      
      if (matchingFile) {
        this.log(`[CHUNK ${chunkIndex}] ✅ Replacing invalid file reference with actual file: ${matchingFile.name} (${matchingFile.size} bytes)`);
        fileToRead = matchingFile;
        // Update both the parameter and the upload manager's reference
        uploadItem.file = matchingFile;
        latestUploadItem.file = matchingFile;
      } else {
        // ❌ CRITICAL: Placeholder file detected - stop immediately, don't retry
        const errorMsg = `FILE MISSING: Please re-select "${latestUploadItem.file?.name || 'unknown'}" to resume upload`;
        this.error(`[CHUNK ${chunkIndex}] ❌ PLACEHOLDER FILE: Cannot resume - file not re-selected after page refresh`);
        // Update status to paused so user can resume after re-selecting file
        this.uploadManager.updateStatus(fileId, 'paused', errorMsg);
        // Throw a special error that won't be retried
        const error: any = new Error(errorMsg);
        error.isPlaceholderError = true; // Mark as non-retryable
        throw error;
      }
    }
    
    // Final validation: ensure file is valid
    if (!fileToRead || fileToRead.size === 0) {
      throw new Error(`Cannot read chunk ${chunkIndex} - file is invalid or empty`);
    }
    
    // Validate byte range is within file bounds
    if (startByte >= fileToRead.size) {
      throw new Error(`Chunk ${chunkIndex} startByte ${startByte} exceeds file size ${fileToRead.size}`);
    }
    
    const expectedChunkSize = endByte - startByte + 1;
    this.log(`[CHUNK ${chunkIndex}] Reading chunk from file: ${fileToRead.name} (${fileToRead.size} bytes), range: ${startByte}-${endByte} (expected size: ${expectedChunkSize} bytes)`);

    // Read chunk using the validated file reference
    const chunk = await this.readFileChunk(fileToRead, startByte, endByte);
    
    // CRITICAL: Validate chunk was read correctly
    // If chunk is empty, it means the file reference is still invalid (placeholder file)
    if (!chunk || chunk.byteLength === 0) {
      // Double-check file reference one more time
      const finalCheckItem = this.uploadManager.getUpload(fileId);
      const finalFile = finalCheckItem?.file;
      
      this.error(`[CHUNK ${chunkIndex}] ❌ Chunk read failed - ArrayBuffer is empty`);
      this.error(`[CHUNK ${chunkIndex}] File details: name="${fileToRead.name}", size=${fileToRead.size}, totalBytes=${latestUploadItem.totalBytes}`);
      this.error(`[CHUNK ${chunkIndex}] Latest file from manager: name="${finalFile?.name || 'null'}", size=${finalFile?.size || 0}`);
      this.error(`[CHUNK ${chunkIndex}] Available files in this.files:`, this.files.map(f => `"${f.name}" (${f.size} bytes)`));
      
      // Try one last time to find and replace the file
      if (finalCheckItem) {
        const lastChanceFile = this.files.find(f => 
          f.name === finalCheckItem.file.name && 
          f.size === finalCheckItem.totalBytes
        );
        
        if (lastChanceFile && lastChanceFile.size > 0) {
          this.log(`[CHUNK ${chunkIndex}] Last chance: Found file, but chunk already read as empty. File needs to be set BEFORE reading chunks.`);
        }
      }
      
      throw new Error(`Chunk ${chunkIndex} read failed - ArrayBuffer is empty. File "${fileToRead.name}" appears to be a placeholder (size: ${fileToRead.size}). Please ensure the actual file is selected before resuming.`);
    }
    
    if (chunk.byteLength !== expectedChunkSize && !isLastChunk) {
      this.warn(`[CHUNK ${chunkIndex}] Chunk size mismatch: expected ${expectedChunkSize} bytes, got ${chunk.byteLength} bytes`);
    }
    
    this.log(`[CHUNK ${chunkIndex}] Successfully read chunk: ${chunk.byteLength} bytes`);

    // Check status after reading
    const afterRead = this.uploadManager.getUpload(fileId);
    if (!afterRead || afterRead.status === 'cancelled' || afterRead.status === 'paused') {
      throw new Error('Upload cancelled or paused');
    }

    // Measure speed on first chunk
    if (chunkIndex === 0) {
      this.speedMeasurementStartTime = Date.now();
      this.speedMeasurementBytes = 0;
    }

    // Upload chunk with retry logic
    const uploadedBytes = await this.uploadChunkWithRetry(
      uploadItem,
      chunk,
      startByte,
      endByte,
      chunkIndex,
      totalChunks,
      isLastChunk
    );

    // Update progress atomically - use maximum to handle parallel chunk completions
    const currentItem = this.uploadManager.getUpload(fileId);
    if (currentItem) {
      // Only update if this chunk reports more bytes than current (handles parallel completions)
      const maxBytes = Math.max(currentItem.bytesUploaded, uploadedBytes);
      this.uploadManager.updateProgress(fileId, maxBytes);
    } else {
      this.uploadManager.updateProgress(fileId, uploadedBytes);
    }

    // Measure speed periodically
    this.speedMeasurementBytes += (endByte - startByte + 1);
    this.speedMeasurementChunkCount++;
    
    if (this.speedMeasurementChunkCount % this.speedMeasurementInterval === 0 || isLastChunk) {
      this.measureUploadSpeed();
    }

    return uploadedBytes;
  }

  /**
   * Measure upload speed and update concurrency limits
   */
  private measureUploadSpeed(): void {
    if (this.speedMeasurementStartTime === 0 || this.speedMeasurementBytes === 0) {
      return;
    }

    const elapsedSeconds = (Date.now() - this.speedMeasurementStartTime) / 1000;
    if (elapsedSeconds <= 0) return;

    const bytesPerSecond = this.speedMeasurementBytes / elapsedSeconds;
    
    // Only update if not manually configured
    if (!this.envConfig?.resumableUpload?.maxConcurrentFiles && 
        !this.envConfig?.resumableUpload?.maxConcurrentChunksPerFile) {
      this.uploadManager.updateUploadSpeed(bytesPerSecond);
    } else {
      // Store speed but don't auto-update limits if manually configured
      this.uploadManager.currentUploadSpeed = bytesPerSecond;
      // Apply manual limits
      if (this.envConfig?.resumableUpload?.maxConcurrentFiles) {
        this.uploadManager.maxConcurrentFiles = this.envConfig.resumableUpload.maxConcurrentFiles;
      }
      if (this.envConfig?.resumableUpload?.maxConcurrentChunksPerFile) {
        this.uploadManager.maxConcurrentChunksPerFile = this.envConfig.resumableUpload.maxConcurrentChunksPerFile;
      }
    }

    // Check if internet is slow and emit event (only once)
    if (this.uploadManager.connectionSpeed === 'slow' && !this.slowInternetMessageShown) {
      this.slowInternetMessageShown = true;
      this.slowInternetDetected.emit();
    }

    // Reset for next measurement
    this.speedMeasurementStartTime = Date.now();
    this.speedMeasurementBytes = 0;
  }

  /**
   * Upload chunk with retry logic
   */
  private async uploadChunkWithRetry(
    uploadItem: UploadFileItem,
    chunk: ArrayBuffer,
    startByte: number,
    endByte: number,
    chunkIndex: number,
    totalChunks: number,
    isLastChunk: boolean
  ): Promise<number> {
    const maxRetries = this.effectiveMaxRetries;
    let lastError: any = null;
    let attempt = 0;
    while (attempt < maxRetries) {
      try {
        // Check if cancelled
        const currentItem = this.uploadManager.getUpload(uploadItem.fileId);
        if (!currentItem || currentItem.status === 'cancelled') {
          throw new Error('Upload cancelled');
        }

        const activeItem = this.uploadManager.getUpload(uploadItem.fileId) ?? uploadItem;
        const streamThroughBackend = this.resolveStreamThroughBackend(activeItem);

        if (streamThroughBackend) {
          if (uploadItem.sessionId) {
            this.log(`[CHUNK ${chunkIndex}] ✅ Using backend API (/api/ResumableUpload/chunk) - RESUME scenario with sessionId=${uploadItem.sessionId}`);
          } else {
            this.log(`[CHUNK ${chunkIndex}] ✅ Using backend API (/api/ResumableUpload/chunk) - NEW upload (no sessionId, but still using backend)`);
          }
        } else {
          this.log(`[CHUNK ${chunkIndex}] Using direct GCS upload (StreamThroughBackend=false)`);
        }

        // CRITICAL: Validate chunk is not empty before uploading
        // This is especially important for resume scenarios where file references might be invalid
        if (!chunk || chunk.byteLength === 0) {
          const errorMsg = `Cannot upload chunk ${chunkIndex} - chunk ArrayBuffer is empty (startByte=${startByte}, endByte=${endByte}, file="${uploadItem.file?.name || 'unknown'}", fileSize=${uploadItem.file?.size || 0}). This usually means the file reference is invalid (placeholder file).`;
          this.error(`[CHUNK ${chunkIndex}] ${errorMsg}`);
          
          // Try to get the latest file reference and re-read the chunk
          const latestItem = this.uploadManager.getUpload(uploadItem.fileId);
          if (latestItem && latestItem.file && latestItem.file.size > 0) {
            this.log(`[CHUNK ${chunkIndex}] Attempting to re-read chunk with latest file reference...`);
            // This shouldn't happen if our validation is working, but add as safety net
            throw new Error(`Chunk ${chunkIndex} is empty. File validation failed. Please ensure the file is selected.`);
          } else {
            throw new Error(errorMsg);
          }
        }

        const uploadedBytes = streamThroughBackend
          ? await this.resumableUploadService.uploadChunkThroughBackend(
              uploadItem.uploadUrl!,
              chunk,
              startByte,
              endByte,
              uploadItem.totalBytes,
              uploadItem.abortController?.signal,
              uploadItem.sessionId,
              chunkIndex
            )
          : await this.resumableUploadService.uploadChunkToGCS(
              uploadItem.uploadUrl!,
              chunk,
              startByte,
              endByte,
              uploadItem.totalBytes,
              uploadItem.abortController?.signal,
              uploadItem.sessionId,
              chunkIndex
            );

        // If uploadedBytes equals totalSize, upload is complete
        if (uploadedBytes >= uploadItem.totalBytes) {
          return uploadedBytes;
        }

        return uploadedBytes;
      } catch (error: any) {
        lastError = error;

        if (error.isUrlExpired) {
          throw error;
        }

        // If offline (or network drop), wait for connectivity and retry without consuming attempts.
        if (this.isOffline() || this.isNetworkError(error)) {
          this.warn(`[CHUNK ${chunkIndex}] Network offline. Waiting to resume...`);
          await this.waitForOnline();
          continue;
        }

        // ❌ CRITICAL: Handle placeholder file error - DO NOT RETRY
        // This prevents thousands of log messages when file isn't re-selected after page refresh
        if (error.isPlaceholderError || error.message?.includes('FILE MISSING') || error.message?.includes('placeholder')) {
          this.error(`[CHUNK ${chunkIndex}] ❌ Placeholder file error - stopping upload. Please re-select the file.`);
          throw error; // Don't retry, throw immediately
        }

        // Handle abort
        if (error.name === 'AbortError' || error.message?.includes('cancelled')) {
          throw error;
        }

        // Handle expired URL
        if (error.message?.includes('expired') || error.message?.includes('404') || error.message?.includes('410')) {
          throw error;
        }

        // Handle CORS error on last chunk - treat as success
        if (isLastChunk && this.isCORSError(error)) {
          this.log('[UPLOAD] Last chunk CORS error - treating as success');
          return uploadItem.totalBytes;
        }

        // Handle status 300 with CORS - treat as success
        if (error.message?.includes('300') && this.isCORSError(error)) {
          this.log('[UPLOAD] Status 300 with CORS - treating as success');
          return uploadItem.totalBytes;
        }

        // Retry with exponential backoff (longer wait times for slow connections)
        if (attempt < maxRetries - 1) {
          const baseWaitTime = Math.pow(2, attempt) * 1000; // Exponential backoff: 1s, 2s, 4s...
          
          // Increase wait time for slow connections
          let waitTime = baseWaitTime;
          const connectionSpeed = this.uploadManager.connectionSpeed;
          if (connectionSpeed === 'slow') {
            waitTime = baseWaitTime * 3; // 3x longer for slow connections: 3s, 6s, 12s...
          } else if (connectionSpeed === 'medium') {
            waitTime = baseWaitTime * 2; // 2x longer for medium connections: 2s, 4s, 8s...
          }
          
          // Cap maximum wait time at 30 seconds
          waitTime = Math.min(waitTime, 30000);
          
          this.warn(`[CHUNK] Upload failed (${connectionSpeed} connection), retrying in ${(waitTime / 1000).toFixed(1)}s... (attempt ${attempt + 1}/${maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, waitTime));
        }
        attempt += 1;
      }
    }

    throw new Error(`Failed to upload chunk after ${maxRetries} retries: ${lastError?.message || lastError}`);
  }

  /**
   * Verify upload with backend
   */
  private async verifyUpload(uploadItem: UploadFileItem): Promise<void> {
    if (!uploadItem.filePath) {
      throw new Error('File path is missing for verification');
    }

    try {
      const verifyResponse = await firstValueFrom(
        this.resumableUploadService.verifyUpload(uploadItem.filePath, uploadItem.totalBytes, uploadItem.sessionId)
      );

      const verifyData = verifyResponse?.data || verifyResponse;
      const status = verifyData?.status || verifyData?.Status;
      const fileSize = verifyData?.fileSize || verifyData?.FileSize;

      if (status === 'Completed' && fileSize === uploadItem.totalBytes) {
        // Upload verified successfully - now complete it (move to File table)
        if (uploadItem.sessionId) {
          try {
            this.log(`[COMPLETE] Calling complete API for session ${uploadItem.sessionId}`);
            const completeResponse = await firstValueFrom(
              this.resumableUploadService.completeUpload(uploadItem.sessionId)
            );
            
            // Handle response - check for data property or direct response
            const completeData = (completeResponse as any)?.data || completeResponse;
            const fileId = completeData?.fileId || completeData?.FileId;
            const status = completeData?.status || completeData?.Status;
            
            if (fileId && status === 'Completed') {
              this.log(`[COMPLETE] Upload completed successfully, file created with ID: ${fileId}`);
              this.log(`[COMPLETE] File stored in File table with all mappings (event, tags, folder)`);
            } else {
              this.warn(`[COMPLETE] Complete API returned but status is not 'Completed':`, completeData);
            }
          } catch (completeError: any) {
            this.error(`[COMPLETE] Error completing upload:`, completeError);
            // Continue even if complete fails - verification was successful
          }
        }

        // Mark as completed and remove from upload list
        this.uploadManager.updateProgress(uploadItem.fileId, uploadItem.totalBytes);
        this.uploadManager.updateStatus(uploadItem.fileId, 'completed');

        // Remove from file picker list after completion
        this.fileRemoved.emit({
          fileName: uploadItem.file.name,
          fileSize: uploadItem.file.size,
          lastModified: uploadItem.file.lastModified
        });

        this.completedSessions.push({
          filePath: uploadItem.filePath,
          fileName: uploadItem.file.name,
          fileSize: uploadItem.totalBytes
        });

        // Clear pending chunks cache for this session (upload is complete)
        if (uploadItem.sessionId) {
          this.pendingChunksCache.delete(uploadItem.sessionId);
          this.log(`[UPLOAD] Cleared pending chunks cache for completed session ${uploadItem.sessionId}`);
        }

        if (this.uploadManager.areAllActiveUploadsCompleted()) {
          this.uploadComplete.emit(this.completedSessions);
        }
        
        // Keep folder-upload file entries in manager until overall completion so
        // aggregate folder progress remains continuous and does not reset per file.
        if (!this.isFolderUploadItem(uploadItem)) {
          setTimeout(() => {
            this.uploadManager.removeUpload(uploadItem.fileId);
          }, 2000);
        }
        
        return;
      }

      throw new Error(`Verification failed - Status: ${status}`);
    } catch (error: any) {
      // If verification fails but bytes match, assume success
      if (uploadItem.bytesUploaded >= uploadItem.totalBytes) {
        this.warn('[VERIFY] Verification failed but bytes match - marking as completed');
        
        // Try to complete the upload (move to File table)
        if (uploadItem.sessionId) {
          try {
            this.log(`[COMPLETE] Calling complete API for session ${uploadItem.sessionId} (fallback)`);
            await firstValueFrom(
              this.resumableUploadService.completeUpload(uploadItem.sessionId)
            );
          } catch (completeError: any) {
            this.error(`[COMPLETE] Error completing upload (fallback):`, completeError);
            // Continue even if complete fails
          }
        }
        
        this.uploadManager.updateProgress(uploadItem.fileId, uploadItem.totalBytes);
        this.uploadManager.updateStatus(uploadItem.fileId, 'completed');

        // Remove from file picker list after completion
        this.fileRemoved.emit({
          fileName: uploadItem.file.name,
          fileSize: uploadItem.file.size,
          lastModified: uploadItem.file.lastModified
        });

        this.completedSessions.push({
          filePath: uploadItem.filePath!,
          fileName: uploadItem.file.name,
          fileSize: uploadItem.totalBytes
        });

        if (this.uploadManager.areAllActiveUploadsCompleted()) {
          this.uploadComplete.emit(this.completedSessions);
        }
        
        // Keep folder-upload file entries in manager until overall completion so
        // aggregate folder progress remains continuous and does not reset per file.
        if (!this.isFolderUploadItem(uploadItem)) {
          setTimeout(() => {
            this.uploadManager.removeUpload(uploadItem.fileId);
          }, 2000);
        }
        
        return;
      }

      throw error;
    }
  }

  /**
   * Check if error is CORS-related
   */
  private isCORSError(error: any): boolean {
    const message = error.message || error.toString() || '';
    return (
      message.includes('Failed to fetch') ||
      message.includes('CORS') ||
      message.includes('NetworkError') ||
      message.includes('Network error') ||
      message.includes('timeout') ||
      message.includes('Timeout')
    );
  }

  /**
   * Check if it's a last chunk CORS error
   */
  private isLastChunkCORSError(error: any, uploadItem: UploadFileItem): boolean {
    if (!this.isCORSError(error)) return false;
    
    const totalChunks = Math.ceil(uploadItem.totalBytes / this.effectiveChunkSize);
    const currentChunk = Math.floor(uploadItem.bytesUploaded / this.effectiveChunkSize);
    return currentChunk >= totalChunks - 1;
  }

  /**
   * Read file chunk
   */
  private readFileChunk(file: File, start: number, end: number): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      // Validate file and parameters
      if (!file) {
        reject(new Error('File is null or undefined'));
        return;
      }

      if (start < 0 || end < 0 || start > end) {
        reject(new Error(`Invalid chunk range: start=${start}, end=${end}`));
        return;
      }

      if (start >= file.size) {
        reject(new Error(`Start byte ${start} exceeds file size ${file.size}`));
        return;
      }

      // Ensure end doesn't exceed file size
      const actualEnd = Math.min(end, file.size - 1);
      const expectedSize = actualEnd - start + 1;

      if (expectedSize <= 0) {
        reject(new Error(`Invalid chunk size: expected ${expectedSize} bytes (start=${start}, end=${actualEnd}, fileSize=${file.size})`));
        return;
      }

      const reader = new FileReader();
      const blob = file.slice(start, actualEnd + 1);
      
      // Validate blob size
      if (blob.size === 0) {
        // ❌ CRITICAL: Blob is empty - this is a placeholder file
        // Don't spam console with repetitive messages - just throw error with clear message
        const error: any = new Error(`FILE MISSING: Please re-select "${file.name}" to resume upload`);
        error.isPlaceholderError = true; // Mark as non-retryable
        reject(error);
        return;
      }
      
      if (blob.size !== expectedSize) {
        this.warn(`[READ CHUNK] Blob size mismatch: expected ${expectedSize} bytes, got ${blob.size} bytes`);
      }
      
      reader.onload = (e) => {
        const result = e.target?.result as ArrayBuffer;
        if (!result || result.byteLength === 0) {
          reject(new Error(`Chunk ArrayBuffer is empty after reading (start=${start}, end=${actualEnd}, expectedSize=${expectedSize})`));
          return;
        }
        if (result.byteLength !== expectedSize && actualEnd < file.size - 1) {
          // For non-last chunks, size must match exactly
          this.warn(`[READ CHUNK] ArrayBuffer size mismatch: expected ${expectedSize} bytes, got ${result.byteLength} bytes`);
        }
        resolve(result);
      };
      reader.onerror = (error) => {
        reject(new Error(`Failed to read chunk: ${error} (start=${start}, end=${actualEnd}, fileSize=${file.size})`));
      };
      reader.readAsArrayBuffer(blob);
    });
  }

  isControlActionLoading(key: string): boolean {
    return this.controlActionLoadingKeys.has(key);
  }

  onPauseUpload(fileId: string): void {
    void runUploadControlAction(
      this.controlActionLoadingKeys,
      uploadControlLoadingKey.filePause(fileId),
      () => this.cdr.detectChanges(),
      () => this.pauseUpload(fileId),
      () => this.uploadManager.getUpload(fileId)?.status === 'paused'
    );
  }

  onPauseFolderUpload(folderName: string): void {
    void runUploadControlAction(
      this.controlActionLoadingKeys,
      uploadControlLoadingKey.folderPause(folderName),
      () => this.cdr.detectChanges(),
      () => this.pauseFolderUpload(folderName),
      () => this.isFolderControlSettledPaused(folderName)
    );
  }

  onResumeFolderUpload(folderName: string): void {
    void runUploadControlAction(
      this.controlActionLoadingKeys,
      uploadControlLoadingKey.folderResume(folderName),
      () => this.cdr.detectChanges(),
      () => this.resumeFolderUpload(folderName),
      () => this.isFolderResumeSettled(folderName),
      { timeoutMs: 2000 }
    );
  }

  onResumeUpload(fileId: string): void {
    void runUploadControlAction(
      this.controlActionLoadingKeys,
      uploadControlLoadingKey.fileResume(fileId),
      () => this.cdr.detectChanges(),
      () => this.resumeUpload(fileId),
      () => this.isFileResumeSettled(fileId),
      { timeoutMs: 2000 }
    );
  }

  private isFileResumeSettled(fileId: string): boolean {
    if (this.isFileControlSettledActive(fileId)) {
      return true;
    }
    return this.uploadManager.getUpload(fileId)?.status === 'paused';
  }

  private isFolderResumeSettled(folderName: string): boolean {
    if (this.isFolderControlSettledActive(folderName)) {
      return true;
    }
    return this.isFolderControlSettledPaused(folderName);
  }

  private isFolderControlSettledPaused(folderName: string): boolean {
    const summary = this.folderUploadSummaries.find(folder => folder.folderName === folderName);
    return summary?.status === 'paused';
  }

  private isFolderControlSettledActive(folderName: string): boolean {
    const summary = this.folderUploadSummaries.find(folder => folder.folderName === folderName);
    return summary?.status === 'uploading' || summary?.status === 'initializing' || summary?.status === 'queued';
  }

  private isFileControlSettledActive(fileId: string): boolean {
    const upload = this.uploadManager.getUpload(fileId);
    if (!upload) {
      return true;
    }
    return upload.status === 'uploading' || upload.status === 'initializing' ||
      (upload.status === 'queued' && !!upload.sessionId);
  }

  /**
   * Pause upload
   */
  private pauseUpload(fileId: string): void {
    this.uploadManager.pauseUpload(fileId);
  }

  private pauseFolderUpload(folderName: string): void {
    const folderUploads = this.uploads.filter(upload => this.getTopFolderName(upload) === folderName);
    folderUploads.forEach(upload => {
      if (upload.status === 'completed' || upload.status === 'cancelled' || upload.status === 'failed') {
        return;
      }
      this.uploadManager.pauseUpload(upload.fileId);
    });
  }

  /**
   * Resume upload (handles both paused and queued with sessionId)
   */
  private resumeUpload(fileId: string): void {
    const upload = this.uploadManager.getUpload(fileId);
    if (!upload) return;
    
    // Handle queued/paused uploads with sessionId (incomplete uploads)
    if ((upload.status === 'queued' || upload.status === 'paused') && upload.sessionId && (upload as any).pendingChunks) {
      this.log(`[RESUME] Resuming ${upload.status} upload with sessionId: ${upload.sessionId}`);
      
      // Check if we have the actual file (not placeholder)
      if (this.isPlaceholderFile(upload.file) || upload.file.size === 0 || upload.file.size !== upload.totalBytes) {
        // Try to find matching real file from input files
        const matchingFile = this.files.find(f => 
          !this.isPlaceholderFile(f) &&
          f.name === upload.file.name && 
          f.size === upload.totalBytes
        );
        
        if (matchingFile) {
          this.log(`[RESUME] Found matching file, replacing placeholder`);
          upload.file = matchingFile;
          this.startResumeUpload(upload);
        } else {
          this.warn(`[RESUME] Cannot resume - file not selected. Please select the file "${upload.file.name}" first.`);
          // Don't try to resume if file isn't available - wait for file to be selected
          // The checkAndAutoResumeFiles will handle it when file is selected
          return;
        }
      } else {
        // Have actual file, can resume
        this.startResumeUpload(upload);
      }
      return;
    }
    
    // Handle paused uploads (without sessionId - regular pause/resume)
    if (upload.status !== 'paused') return;

    this.uploadManager.resumeUpload(fileId);
    this.resumeOnlyMode = false;
    this.pendingAutoResume = false;
    if (!this.isProcessingQueue) {
      void this.processQueue();
    }
  }

  private resumeFolderUpload(folderName: string): void {
    const folderUploads = this.uploads.filter(upload => this.getTopFolderName(upload) === folderName);
    let resumedAny = false;
    folderUploads.forEach(upload => {
      if (upload.status !== 'paused') {
        return;
      }
      this.uploadManager.resumeUpload(upload.fileId);
      resumedAny = true;
    });
    if (!resumedAny) {
      return;
    }
    this.resumeOnlyMode = false;
    this.pendingAutoResume = false;
    if (!this.isProcessingQueue) {
      void this.processQueue();
    }
  }

  /**
   * Start resume upload (common logic for paused and queued resumes)
   */
  private startResumeUpload(upload: UploadFileItem): void {
    this.enqueueResume(upload);
  }

  /**
   * Internal resume runner (throttled/queued by enqueueResume)
   */
  private async startResumeUploadInternal(upload: UploadFileItem): Promise<void> {
    const fileId = upload.fileId;
    
    // CRITICAL: Validate file before starting resume
    // During resume, we might have a placeholder file (size 0) that needs to be replaced
    if (!upload.file || this.isPlaceholderFile(upload.file) || upload.file.size === 0 || upload.file.size !== upload.totalBytes) {
      this.warn(`[RESUME] Invalid file reference for resume: file=${!!upload.file}, fileSize=${upload.file?.size || 0}, totalBytes=${upload.totalBytes}, fileName=${upload.file?.name || 'unknown'}`);
      this.log(`[RESUME] Available files in this.files: ${this.files.length} files`, this.files.map(f => `${f.name} (${f.size} bytes)`));
      
      // Try to find matching file from input files
      const matchingFile = this.files.find(f => 
        !this.isPlaceholderFile(f) &&
        f.name === upload.file.name && 
        f.size === upload.totalBytes
      );
      
      if (matchingFile) {
        this.log(`[RESUME] ✅ Found matching file, replacing placeholder: ${matchingFile.name} (${matchingFile.size} bytes)`);
        upload.file = matchingFile;
        // Update the upload manager's reference as well
        const currentUpload = this.uploadManager.getUpload(fileId);
        if (currentUpload) {
          currentUpload.file = matchingFile;
          this.log(`[RESUME] ✅ Updated upload manager file reference`);
        }
      } else {
        this.error(`[RESUME] ❌ Cannot resume - file "${upload.file.name}" (${upload.totalBytes} bytes) not found in selected files (${this.files.length} files available).`);
        this.error(`[RESUME] Available files:`, this.files.map(f => `"${f.name}" (${f.size} bytes)`));
        this.error(`[RESUME] Please select the file "${upload.file.name}" first to resume the upload.`);
        this.uploadManager.updateStatus(fileId, 'queued');
        return;
      }
    }
    
    // Final validation: ensure file is valid before proceeding
    if (!upload.file || this.isPlaceholderFile(upload.file) || upload.file.size === 0) {
      this.error(`[RESUME] ❌ Cannot resume - file is invalid or empty: ${upload.file?.name || 'unknown'}`);
      this.uploadManager.updateStatus(fileId, 'queued');
      return;
    }
    
    // Log file validation success
    this.log(`[RESUME] ✅ File validated: ${upload.file.name} (${upload.file.size} bytes), ready to start upload`);
    
    // Check if we can start this upload (parallel upload support)
    if (this.uploadManager.canStartUpload(fileId)) {
      this.uploadManager.addActiveUploading(fileId);
      this.uploadManager.updateStatus(fileId, 'uploading');
      
      // Get the latest upload item to ensure we have the correct file reference and streamThroughBackend flag
      const latestUpload = this.uploadManager.getUpload(fileId);
      if (!latestUpload) {
        this.error(`[RESUME] Upload item not found: ${fileId}`);
        this.uploadManager.removeActiveUploading(fileId);
        return;
      }
      
      // Start uploading with the latest upload item (ensures file reference is correct)
      try {
        await this.uploadFile(latestUpload);
      } catch (error) {
        this.error(`[RESUME] Error resuming upload:`, error);
      } finally {
        this.uploadManager.removeActiveUploading(fileId);
        if (!this.isProcessingQueue) {
          this.processQueue();
        }
      }
    } else {
      // Queue it for later (will be picked up by queue processor)
      this.uploadManager.updateStatus(fileId, 'queued');
      if (!this.isProcessingQueue) {
        this.processQueue();
      }
    }
  }

  /**
   * Cancel upload
   */
  confirmCancelUpload(fileId: string): void {
    const upload = this.uploadManager.getUpload(fileId);
    const itemName = upload?.file?.name || 'this item';
    this.confirmationService.confirm({
      message: this.translateService.instant('MEDIALIBRARY.removeUploadItemConfirmation', { itemName }),
      header: this.translateService.instant('MEDIALIBRARY.removeUploadItemHeader'),
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.cancelUpload(fileId),
    });
  }

  confirmRemoveFolderUpload(folderName: string): void {
    this.confirmationService.confirm({
      message: this.translateService.instant('MEDIALIBRARY.removeUploadItemConfirmation', { itemName: folderName }),
      header: this.translateService.instant('MEDIALIBRARY.removeUploadItemHeader'),
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.removeFolderUpload(folderName),
    });
  }

  confirmRemoveFile(fileId: string): void {
    const upload = this.uploadManager.getUpload(fileId);
    const itemName = upload?.file?.name || 'this item';
    this.confirmationService.confirm({
      message: this.translateService.instant('MEDIALIBRARY.removeUploadItemConfirmation', { itemName }),
      header: this.translateService.instant('MEDIALIBRARY.removeUploadItemHeader'),
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.removeFile(fileId),
    });
  }

  cancelUpload(fileId: string): void {
    const upload = this.uploadManager.getUpload(fileId);
    if (upload) {
      // If there's a sessionId, mark session as Cancelled in database
      if (upload.sessionId) {
        this.log(`[CANCEL] Cancelling session ${upload.sessionId} for file: ${upload.file.name}`);
        // Mark session as Cancelled in backend so it appears in incomplete uploads
        this.resumableUploadService.cancelUpload(upload.sessionId).subscribe({
          next: () => {
            this.log(`[CANCEL] Session ${upload.sessionId} marked as Cancelled in database`);
            // Invalidate cache so incomplete uploads refresh
            this.resumableUploadService.invalidateIncompleteUploadsCache();
          },
          error: (error) => {
            this.error(`[CANCEL] Error cancelling session ${upload.sessionId}:`, error);
            // Continue with local cancel even if backend call fails
          }
        });
      }
      
      // Emit file info before removing so parent can remove from file picker
      this.fileRemoved.emit({
        fileName: upload.file.name,
        fileSize: upload.file.size,
        lastModified: upload.file.lastModified
      });
    }
    this.uploadManager.cancelUpload(fileId);
  }

  cancelFolderUpload(folderName: string): void {
    const folderUploads = this.uploads.filter(upload => this.getTopFolderName(upload) === folderName);
    folderUploads.forEach(upload => this.cancelUpload(upload.fileId));
  }

  /**
   * Remove file from queue
   */
  removeFile(fileId: string): void {
    const upload = this.uploadManager.getUpload(fileId);
    if (upload) {
      // Emit file info before removing so parent can remove from file picker
      this.fileRemoved.emit({
        fileName: upload.file.name,
        fileSize: upload.file.size,
        lastModified: upload.file.lastModified
      });
    }
    this.uploadManager.removeUpload(fileId);
  }

  removeFolderUpload(folderName: string): void {
    const folderUploads = this.uploads.filter(upload => this.getTopFolderName(upload) === folderName);
    folderUploads.forEach(upload => {
      const normalizedStatus = this.getNormalizedUploadStatus(upload);
      if (
        normalizedStatus === 'uploading' ||
        normalizedStatus === 'initializing' ||
        normalizedStatus === 'queued' ||
        normalizedStatus === 'paused'
      ) {
        this.cancelUpload(upload.fileId);
        this.uploadManager.removeUpload(upload.fileId);
        return;
      }
      this.removeFile(upload.fileId);
    });
  }

  /**
   * Check for incomplete uploads and resume them
   */
  private async checkAndResumeIncompleteUploads(): Promise<void> {
    try {
      const incompleteSessions = await firstValueFrom(
        this.resumableUploadService.getIncompleteUploads()
      );
      
      if (incompleteSessions.length === 0) {
        return; // No incomplete uploads
      }
      
      this.log(`[RESUME] Found ${incompleteSessions.length} incomplete upload(s)`);
      
      // Load incomplete sessions into upload manager for display
      await this.loadIncompleteSessions(incompleteSessions);
      
      // Do NOT auto-resume on load; only prepare list for UI
    } catch (error) {
      this.error('[RESUME] Error checking incomplete uploads:', error);
    }
  }
  
  /**
   * Public method to check and resume incomplete uploads (can be called from parent component)
   */
  public async checkAndResumeIncompleteUploadsPublic(): Promise<void> {
    await this.checkAndResumeIncompleteUploads();
  }

  /**
   * Public method to check and auto-resume files (can be called from parent component)
   */
  public async checkAndAutoResumeFilesPublic(files: File[]): Promise<void> {
    await this.checkAndAutoResumeFiles(files, false);
  }

  /**
   * Check if files match incomplete sessions and auto-resume
   */
  private async checkAndAutoResumeFiles(files: File[], autoStart: boolean): Promise<boolean> {
    let didAutoResume = false;
    try {
      const incompleteSessions = await firstValueFrom(
        this.resumableUploadService.getIncompleteUploads()
      );
      
      if (incompleteSessions.length === 0 || files.length === 0) {
        return false;
      }
      
      for (const file of files) {
        if ((file as any).__isPlaceholder) {
          continue;
        }
        // Find matching incomplete session by filename and size
        const matchingSession = incompleteSessions.find(session => 
          session.fileName === file.name && 
          session.fileSize === file.size &&
          session.expiresOn && 
          new Date(session.expiresOn) > new Date()
        );
        
        if (matchingSession && matchingSession.sessionId) {
          this.log(`[RESUME] Found matching session for ${file.name}, preparing resume...`);
          
          const sessionIdStr = matchingSession.sessionId.toString();
          
          // Get pending chunks (use cache if available to avoid duplicate API calls)
          let pendingChunks: number[];
          if (this.pendingChunksCache.has(sessionIdStr)) {
            pendingChunks = this.pendingChunksCache.get(sessionIdStr)!;
            this.log(`[RESUME] Using cached pending chunks for session ${sessionIdStr}`);
          } else {
            pendingChunks = await firstValueFrom(
              this.resumableUploadService.getPendingChunks(matchingSession.sessionId)
            );
            // Cache the result
            this.pendingChunksCache.set(sessionIdStr, pendingChunks);
            this.log(`[RESUME] Fetched and cached pending chunks for session ${sessionIdStr}: ${pendingChunks.length} pending`);
          }
        
        if (pendingChunks.length === 0) {
          // All chunks complete, verify upload
          try {
            await this.resumableUploadService.verifyUpload(
                matchingSession.filePath || '',
                matchingSession.fileSize || 0,
                matchingSession.sessionId
            ).toPromise();
              this.log(`[RESUME] Upload verified for ${file.name}`);
          } catch (error) {
              this.error(`[RESUME] Verification failed for ${file.name}:`, error);
          }
          continue;
        }
        
          // Find the upload item for this file
          // First try to find by sessionId (from loadIncompleteSessions) - this is more reliable
          const allUploads = this.uploadManager.getAllUploads();
          let uploadItem: UploadFileItem | undefined;
          
          if (matchingSession.sessionId) {
            uploadItem = allUploads.find(u => 
              u.sessionId === matchingSession.sessionId.toString()
            );
            
            // If found by sessionId, replace placeholder file with actual file
            if (uploadItem) {
              this.log(`[RESUME] Found upload by sessionId, replacing placeholder file`);
              uploadItem.file = file;
              // Update totalBytes to match actual file size
              uploadItem.totalBytes = file.size;
            }
          }
          
          // If not found by sessionId, try to find by name and size (ignore lastModified for resume)
          if (!uploadItem) {
            uploadItem = allUploads.find(u => 
              u.file.name === file.name && 
              u.totalBytes === file.size
            );
            
            // If found, update the file reference
            if (uploadItem) {
              this.log(`[RESUME] Found upload by name/size, updating file reference`);
              uploadItem.file = file;
            }
          }
          
          if (uploadItem) {
            // Update with session data FIRST
            this.uploadManager.updateSessionData(uploadItem.fileId, {
              uploadUrl: matchingSession.uploadUrl,
              filePath: matchingSession.filePath,
              chunkSize: matchingSession.chunkSize || this.effectiveChunkSize,
              sessionId: matchingSession.sessionId.toString(),
              bytesUploaded: matchingSession.bytesUploaded || 0
            });
            
            const currentItem = this.uploadManager.getUpload(uploadItem.fileId);
            if (currentItem && currentItem.streamThroughBackend === undefined) {
              currentItem.streamThroughBackend = this.resolveStreamThroughBackend(currentItem);
            }
            
            // Store pending chunks
            (uploadItem as any).pendingChunks = pendingChunks;
            
            this.log(`[RESUME] Ready to resume ${file.name} with ${pendingChunks.length} pending chunks, chunkSize: ${matchingSession.chunkSize || this.effectiveChunkSize}`);
            
            // Update progress to show actual progress
            this.uploadManager.updateProgress(uploadItem.fileId, matchingSession.bytesUploaded || 0);
            
            // Do not auto-start; mark as paused until user clicks resume
            this.uploadManager.updateStatus(uploadItem.fileId, autoStart ? 'uploading' : 'paused');
            
            if (autoStart) {
              // Ensure session data is set before enqueueing resume
              setTimeout(() => {
                const currentUpload = this.uploadManager.getUpload(uploadItem!.fileId);
                if (currentUpload) {
                  if (!currentUpload.uploadUrl || !currentUpload.filePath) {
                    this.warn(`[RESUME] Session data not set on upload item, setting again...`);
                    this.uploadManager.updateSessionData(currentUpload.fileId, {
                      uploadUrl: matchingSession.uploadUrl,
                      filePath: matchingSession.filePath,
                      chunkSize: matchingSession.chunkSize || this.effectiveChunkSize,
                      sessionId: matchingSession.sessionId.toString(),
                      bytesUploaded: matchingSession.bytesUploaded || 0
                    });
                  }
                  const updatedUpload = this.uploadManager.getUpload(uploadItem!.fileId);
                  if (updatedUpload && updatedUpload.status === 'uploading') {
                    this.log(`[RESUME] Enqueuing resume upload with session data: uploadUrl=${!!updatedUpload.uploadUrl}, filePath=${!!updatedUpload.filePath}, sessionId=${updatedUpload.sessionId}`);
                    this.enqueueResume(updatedUpload);
                  }
                }
              }, 100);
              didAutoResume = true;
            }
          } else {
            // File not in queue yet, add it with session data
            this.log(`[RESUME] Adding file ${file.name} to queue with session data`);
            const metadata = {
              folderId: matchingSession.folderId,
              titleEn: matchingSession.titleEn,
              titleAr: matchingSession.titleAr,
              eventId: matchingSession.eventId,
              eventDate: matchingSession.fileCreationDate || matchingSession.eventDate,
              locationId: matchingSession.locationId,
              newLocation: matchingSession.newLocation,
              tagIds: typeof matchingSession.tagIds === 'string' && matchingSession.tagIds.trim()
                ? (matchingSession.tagIds.trim().startsWith('[') ? JSON.parse(matchingSession.tagIds) : matchingSession.tagIds.split(',').map((id: string) => parseInt(id.trim())).filter((id: number) => !isNaN(id)))
                : [],
              newTags: typeof matchingSession.newTags === 'string' && matchingSession.newTags.trim()
                ? (matchingSession.newTags.trim().startsWith('[') ? JSON.parse(matchingSession.newTags) : matchingSession.newTags.split(',').map((t: string) => t.trim()).filter((t: string) => t.length > 0))
                : [],
              newEvent: matchingSession.newEvent,
              isMarkSecure: matchingSession.isMarkSecure,
              userEmails: matchingSession.userEmails,
              shareType: matchingSession.shareType as 'internal' | 'external' | undefined,
              shareUserIds: this.parseNumberArray(matchingSession.userIds),
              shareEmails: this.parseStringArray(matchingSession.emails ?? matchingSession.userEmails),
              shareExpireOn: matchingSession.expireOn,
              userId: matchingSession.userId,
              fileCreationDate: matchingSession.fileCreationDate || matchingSession.eventDate
            };
            
            const fileIds = this.uploadManager.addFiles([file], metadata);
            if (fileIds.length > 0) {
              const newFileId = fileIds[0];
              const newUpload = this.uploadManager.getUpload(newFileId);
              if (newUpload) {
                // Update with session data - use chunkSize from database
                this.uploadManager.updateSessionData(newFileId, {
                  uploadUrl: matchingSession.uploadUrl,
                  filePath: matchingSession.filePath,
                  chunkSize: matchingSession.chunkSize || this.effectiveChunkSize, // Use chunkSize from DB
                  sessionId: matchingSession.sessionId.toString(),
                  bytesUploaded: matchingSession.bytesUploaded || 0
                });
                
                const updatedNewUpload = this.uploadManager.getUpload(newFileId);
                if (updatedNewUpload && updatedNewUpload.streamThroughBackend === undefined) {
                  updatedNewUpload.streamThroughBackend = this.resolveStreamThroughBackend(updatedNewUpload);
                }
                
                // Store pending chunks
                (newUpload as any).pendingChunks = pendingChunks;
                
                // Update progress
                this.uploadManager.updateProgress(newFileId, matchingSession.bytesUploaded || 0);
                
                // Do not auto-start; mark as paused until user clicks resume
                this.uploadManager.updateStatus(newFileId, autoStart ? 'uploading' : 'paused');
                
                if (autoStart) {
                  // Enqueue resume with throttling after session data is confirmed
                  setTimeout(() => {
                    const currentUpload = this.uploadManager.getUpload(newFileId);
                    if (currentUpload) {
                      if (!currentUpload.uploadUrl || !currentUpload.filePath) {
                        this.warn(`[RESUME] Session data not set on new upload item, setting again...`);
                        this.uploadManager.updateSessionData(newFileId, {
                          uploadUrl: matchingSession.uploadUrl,
                          filePath: matchingSession.filePath,
                          chunkSize: matchingSession.chunkSize || this.effectiveChunkSize,
                          sessionId: matchingSession.sessionId.toString(),
                          bytesUploaded: matchingSession.bytesUploaded || 0
                        });
                      }
                      const updatedUpload = this.uploadManager.getUpload(newFileId);
                      if (updatedUpload && updatedUpload.status === 'uploading') {
                        this.log(`[RESUME] Enqueuing resume upload for new file: uploadUrl=${!!updatedUpload.uploadUrl}, filePath=${!!updatedUpload.filePath}, sessionId=${updatedUpload.sessionId}`);
                        this.enqueueResume(updatedUpload);
                      }
                    }
                  }, 100);
                  didAutoResume = true;
                }
              }
            }
          }
        }
      }
    } catch (error) {
      this.error('[RESUME] Error preparing resume:', error);
    }
    return didAutoResume;
  }
  
  /**
   * Trigger resume for pending uploads (user action only).
   */
  public async triggerResumePendingUploads(): Promise<void> {
    if (this.resumeStartedOnce) {
      return;
    }
    // User-triggered resume should allow normal queue processing
    this.resumeOnlyMode = false;
    this.pendingAutoResume = false;
    this.resumeStartedOnce = true;
    await this.checkAndAutoResumeFiles(this.files || [], true);
  }

  /**
   * Enqueue resume with throttling and concurrency limits.
   */
  private enqueueResume(upload: UploadFileItem): void {
    if (this.resumeQueueIds.has(upload.fileId)) {
      return;
    }
    this.resumeQueueIds.add(upload.fileId);
    this.resumeQueue.push(upload);
    this.processResumeQueue();
  }

  private async processResumeQueue(): Promise<void> {
    if (this.isProcessingResumeQueue) {
      return;
    }
    this.isProcessingResumeQueue = true;
    this.applyResumeConcurrencyLimits();
    try {
      while (this.resumeQueue.length > 0) {
        const next = this.resumeQueue.shift();
        if (!next) {
          break;
        }
        this.resumeQueueIds.delete(next.fileId);
        await new Promise(resolve => setTimeout(resolve, this.resumeThrottleMs));
        await this.startResumeUploadInternal(next);
      }
    } finally {
      this.isProcessingResumeQueue = false;
      this.restoreConcurrencyLimitsIfIdle();
      if (this.pendingAutoResume && this.resumeQueue.length === 0) {
        this.resumeOnlyMode = false;
        this.pendingAutoResume = false;
      }
    }
  }

  /**
   * Auto-resume matching sessions without starting new uploads.
   */
  private async autoResumeIfEnabled(files: File[]): Promise<void> {
    if (!this.autoResume) {
      return;
    }
    this.resumeOnlyMode = true;
    this.pendingAutoResume = true;
    const didAutoResume = await this.checkAndAutoResumeFiles(files, true);
    if (!didAutoResume) {
      this.resumeOnlyMode = false;
      this.pendingAutoResume = false;
    }
  }

  /**
   * Select next queued upload (optionally resume-only).
   */
  private getNextQueuedUpload(): UploadFileItem | null {
    if (!this.resumeOnlyMode) {
      return this.uploadManager.getNextQueuedFile();
    }
    const uploads = this.uploadManager.getAllUploads();
    const queuedResume = uploads.find(u =>
      u.status === 'queued' &&
      u.sessionId &&
      (u as any).pendingChunks
    );
    if (queuedResume) {
      return queuedResume;
    }
    const failedResume = uploads.find(u =>
      u.status === 'failed' &&
      u.sessionId &&
      (u.retryCount || 0) < this.effectiveMaxRetries
    );
    return failedResume || null;
  }

  private applyResumeConcurrencyLimits(): void {
    if (!this.previousConcurrencyLimits) {
      this.previousConcurrencyLimits = {
        files: this.uploadManager.maxConcurrentFiles,
        chunks: this.uploadManager.maxConcurrentChunksPerFile
      };
    }
    this.uploadManager.maxConcurrentFiles = Math.min(
      this.uploadManager.maxConcurrentFiles,
      this.resumeConcurrencyLimit
    );
    this.uploadManager.maxConcurrentChunksPerFile = Math.min(
      this.uploadManager.maxConcurrentChunksPerFile,
      this.resumeChunkConcurrencyLimit
    );
  }

  private restoreConcurrencyLimitsIfIdle(): void {
    if (this.previousConcurrencyLimits && this.resumeQueue.length === 0) {
      this.uploadManager.maxConcurrentFiles = this.previousConcurrencyLimits.files;
      this.uploadManager.maxConcurrentChunksPerFile = this.previousConcurrencyLimits.chunks;
      this.previousConcurrencyLimits = null;
    }
  }

  /**
   * Load incomplete sessions and display them in the upload list with progress
   * Cache pending chunks to avoid duplicate API calls
   */
  private pendingChunksCache = new Map<string, number[]>();
  
  public async loadIncompleteSessions(sessions: ResumableUploadSession[]): Promise<void> {
    try {
      this.log(`[RESUME] Loading ${sessions.length} incomplete session(s) into upload list`);
      
      // Clear cache for sessions that are no longer incomplete
      const currentSessionIds = new Set(sessions.map(s => s.sessionId?.toString()).filter(Boolean));
      for (const [sessionId] of this.pendingChunksCache) {
        if (!currentSessionIds.has(sessionId)) {
          this.pendingChunksCache.delete(sessionId);
        }
      }
      
      for (const session of sessions) {
        // Check if upload URL is still valid (not expired)
        if (session.expiresOn && new Date(session.expiresOn) < new Date()) {
          this.warn(`[RESUME] Session ${session.sessionId} expired, skipping`);
          continue;
        }
        
        if (!session.sessionId) continue;
        
        const sessionIdStr = session.sessionId.toString();
        
        // Get pending chunks to calculate progress (use cache if available)
        let pendingChunks: number[];
        if (this.pendingChunksCache.has(sessionIdStr)) {
          pendingChunks = this.pendingChunksCache.get(sessionIdStr)!;
          this.log(`[RESUME] Using cached pending chunks for session ${sessionIdStr}`);
        } else {
          pendingChunks = await firstValueFrom(
            this.resumableUploadService.getPendingChunks(session.sessionId)
          );
          // Cache the result
          this.pendingChunksCache.set(sessionIdStr, pendingChunks);
          this.log(`[RESUME] Fetched and cached pending chunks for session ${sessionIdStr}: ${pendingChunks.length} pending`);
        }
        
        // Calculate progress based on completed chunks
        const totalChunks = session.totalChunks || 0;
        const completedChunks = totalChunks - pendingChunks.length;
        const progress = totalChunks > 0 ? (completedChunks / totalChunks) * 100 : 0;
        const estimatedBytesUploaded = session.bytesUploaded && session.bytesUploaded > 0
          ? session.bytesUploaded
          : Math.min(session.fileSize, completedChunks * (session.chunkSize || this.effectiveChunkSize));
        
        // Create a File object from session info (for display purposes)
        // We'll need the actual file when resuming, but for now create a placeholder
        const file = new File([], session.fileName, { 
          type: session.contentType || 'application/octet-stream' 
        });
        
        // Check if upload already exists
        const allUploads = this.uploadManager.getAllUploads();
        const existingUpload = allUploads.find(u => u.sessionId === session.sessionId.toString());
        
        if (!existingUpload) {
          // Create metadata object
          const metadata: UploadFileItem['metadata'] = {
            folderId: session.folderId,
            titleEn: session.titleEn,
            titleAr: session.titleAr,
            eventId: session.eventId,
            eventDate: session.fileCreationDate || session.eventDate ? new Date(session.fileCreationDate || session.eventDate || '').toISOString() : undefined,
            locationId: session.locationId,
            newLocation: session.newLocation,
            tagIds: typeof session.tagIds === 'string' && session.tagIds.trim() 
              ? (session.tagIds.trim().startsWith('[') ? JSON.parse(session.tagIds) : session.tagIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id)))
              : (Array.isArray(session.tagIds) ? session.tagIds : []),
            newTags: typeof session.newTags === 'string' && session.newTags.trim()
              ? (session.newTags.trim().startsWith('[') ? JSON.parse(session.newTags) : session.newTags.split(',').map(t => t.trim()).filter(t => t.length > 0))
              : (Array.isArray(session.newTags) ? session.newTags : []),
            newEvent: session.newEvent,
            isMarkSecure: session.isMarkSecure,
            userEmails: session.userEmails,
            shareType: session.shareType as 'internal' | 'external' | undefined,
            shareUserIds: this.parseNumberArray(session.userIds),
            shareEmails: this.parseStringArray(session.emails ?? session.userEmails),
            shareExpireOn: session.expireOn,
            userId: session.userId,
            fileCreationDate: session.fileCreationDate ? new Date(session.fileCreationDate).toISOString() : (session.eventDate ? new Date(session.eventDate).toISOString() : undefined)
          };
          
          // Add file to upload manager (unique by sessionId)
          const fileId = this.uploadManager.addResumableSession(file, session.sessionId.toString(), metadata);
          
          if (fileId) {
            const upload = this.uploadManager.getUpload(fileId);
            
            if (upload) {
              // Fix totalBytes - placeholder file has size 0, but we know the actual size from session
              upload.totalBytes = session.fileSize;
              
              // Update with session data - use chunkSize from database
              this.uploadManager.updateSessionData(fileId, {
                uploadUrl: session.uploadUrl,
                filePath: session.filePath,
                chunkSize: session.chunkSize || this.effectiveChunkSize, // Use chunkSize from DB
                sessionId: session.sessionId.toString(),
                bytesUploaded: estimatedBytesUploaded
              });
              
              if (upload.streamThroughBackend === undefined) {
                upload.streamThroughBackend = this.resolveStreamThroughBackend(upload);
              }
              this.log(`[RESUME] streamThroughBackend=${upload.streamThroughBackend} for loaded session ${session.sessionId}`);
              
              // Update progress to show actual progress
              this.uploadManager.updateProgress(fileId, estimatedBytesUploaded);
              
              // Store pending chunks for resume
              (upload as any).pendingChunks = pendingChunks;
              
              // Check if all chunks are completed
              if (pendingChunks.length === 0) {
                this.uploadManager.updateStatus(fileId, 'completed');
                this.log(`[RESUME] Session ${session.sessionId} already completed`);
                continue;
              }
              
              // Try to find matching file (already selected)
              const matchingFile = this.files.find(f => 
                f.name === session.fileName && 
                f.size === session.fileSize
              );
              
              if (matchingFile && pendingChunks.length > 0) {
                this.log(`[RESUME] File ${session.fileName} matches incomplete session; waiting for user action to resume`);
                // Replace placeholder file with actual file
                upload.file = matchingFile;
                
                // Do not auto-start; keep paused until user clicks resume
                this.uploadManager.updateStatus(fileId, 'paused');
              } else if (pendingChunks.length > 0) {
                // File not selected yet - wait for user to select and click resume
                this.uploadManager.updateStatus(fileId, 'paused');
                this.log(`[RESUME] Session ${session.sessionId} ready for manual resume when file is selected (${pendingChunks.length} pending chunks, ${progress.toFixed(1)}% complete)`);
              }
            }
          }
        }
        
        this.log(`[RESUME] Loaded session ${session.sessionId}: ${progress.toFixed(1)}% complete, ${pendingChunks.length} pending chunks`);
      }
      
      // Trigger change detection
      this.cdr.detectChanges();
    } catch (error) {
      this.error('[RESUME] Error loading incomplete sessions:', error);
    }
  }

  /**
   * Retry failed upload
   */
  retryUpload(fileId: string): void {
    const upload = this.uploadManager.getUpload(fileId);
    if (!upload || upload.status !== 'failed') return;

    // Reset and requeue
    this.uploadManager.updateStatus(fileId, 'queued');
    this.uploadManager.updateProgress(fileId, 0);
    
    // Restart queue processing
    if (!this.isProcessingQueue) {
      this.processQueue();
    }
  }

  /**
   * Format bytes
   */
  formatBytes(bytes: number, decimals: number = 2): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  /**
   * Get status display text
   */
  getStatusText(status: UploadStatus): string {
    const statusMap: Record<UploadStatus, string> = {
      queued: 'Queued',
      initializing: 'Initializing',
      uploading: 'Uploading',
      paused: 'Paused',
      completed: 'Completed',
      failed: 'Failed',
      cancelled: 'Cancelled'
    };
    return statusMap[status] || status;
  }

  private getNormalizedUploadStatus(upload: UploadFileItem): UploadStatus {
    if (upload.status === 'queued' && upload.progress > 0 && upload.sessionId) {
      return 'uploading';
    }
    if (upload.status === 'queued' && upload.progress > 0 && !upload.sessionId) {
      return 'paused';
    }
    return upload.status;
  }

  private getRelativeUploadPath(upload: UploadFileItem): string {
    const relativePath = (
      (upload.file as any)?.__currentRelativePath ||
      (upload.file as any)?.webkitRelativePath ||
      ''
    ).replace(/\\/g, '/').trim();
    return relativePath || upload.file.name;
  }

  private getTopFolderName(upload: UploadFileItem): string | null {
    const relativePath = this.getRelativeUploadPath(upload);
    const parts = relativePath.split('/').filter(Boolean);
    if (parts.length <= 1) {
      return null;
    }
    return parts[0];
  }

  private isFolderUploadItem(upload: UploadFileItem): boolean {
    return !!this.getTopFolderName(upload);
  }

  private getDisplayState(upload: UploadFileItem): 'pending' | 'uploading' | 'uploaded' | 'failed' {
    if (upload.status === 'completed') return 'uploaded';
    if (upload.status === 'failed' || upload.status === 'cancelled') return 'failed';
    if (upload.status === 'uploading' || upload.status === 'initializing') return 'uploading';
    if (upload.status === 'queued' && upload.progress > 0 && upload.sessionId) return 'uploading';
    return 'pending';
  }

  private buildFolderUploadSummaries(uploads: UploadFileItem[]): FolderUploadSummary[] {
    const map = new Map<string, {
      fileIds: string[];
      totalItems: number;
      uploadedItems: number;
      totalBytes: number;
      uploadedBytes: number;
      statusCounts: Record<UploadStatus, number>;
    }>();
    uploads.forEach(upload => {
      const folderName = this.getTopFolderName(upload);
      if (!folderName) {
        return;
      }
      if (!map.has(folderName)) {
        map.set(folderName, {
          fileIds: [],
          totalItems: 0,
          uploadedItems: 0,
          totalBytes: 0,
          uploadedBytes: 0,
          statusCounts: {
            queued: 0,
            initializing: 0,
            uploading: 0,
            paused: 0,
            completed: 0,
            failed: 0,
            cancelled: 0
          }
        });
      }
      const current = map.get(folderName)!;
      const normalizedStatus = this.getNormalizedUploadStatus(upload);
      current.fileIds.push(upload.fileId);
      current.totalItems += 1;
      current.totalBytes += upload.totalBytes || 0;
      current.uploadedBytes += upload.bytesUploaded || 0;
      current.statusCounts[normalizedStatus] += 1;
      const state = this.getDisplayState(upload);
      if (state === 'uploaded') {
        current.uploadedItems += 1;
      }
    });

    return Array.from(map.entries())
      .map(([folderName, value]) => ({
        folderName,
        fileIds: value.fileIds,
        uploadedItems: value.uploadedItems,
        totalItems: value.totalItems,
        uploadedBytes: value.uploadedBytes,
        totalBytes: value.totalBytes,
        progress: value.totalItems > 0 ? Math.min(100, (value.uploadedItems / value.totalItems) * 100) : 0,
        status: this.getFolderAggregateStatus(value.statusCounts, value.totalItems),
        isComplete: value.totalItems > 0 && value.uploadedItems === value.totalItems,
      }))
      .sort((a, b) => a.folderName.localeCompare(b.folderName));
  }

  private getFolderAggregateStatus(
    statusCounts: Record<UploadStatus, number>,
    totalItems: number
  ): UploadStatus {
    if (statusCounts.completed === totalItems && totalItems > 0) return 'completed';
    if (statusCounts.cancelled === totalItems && totalItems > 0) return 'cancelled';
    if (statusCounts.uploading > 0 || statusCounts.initializing > 0) return 'uploading';
    if (statusCounts.paused > 0) return 'paused';
    if (statusCounts.queued > 0) return 'queued';
    if (statusCounts.failed > 0) return 'failed';
    return 'queued';
  }
}




